/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : EEComponents.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import CloseIcon from "@mui/icons-material/Close";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";

import TextField from "@mui/material/TextField";

import Typography from "@mui/material/Typography";
import React, { useCallback, useEffect, useRef, useState } from "react";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import VlcSWModules from "./SWModules";
import {
  adminSettingsAPI,
  HandleApiActions,
  labCar,
} from "../../redux/actions";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import customConsole from "../../config/customConsole";
import { useLocation, useNavigate } from "react-router-dom";
import editRequestAPI from "../../redux/actions/editRequestApi";

import LockIcon from "@mui/icons-material/Lock";
import NoEncryptionGmailerrorredIcon from "@mui/icons-material/NoEncryptionGmailerrorred";
import moment from "moment";
import actionType from "../../redux/actionTypes";
import { AppBar, InputAdornment, Toolbar } from "@mui/material";
import ruleEngineActions from "../../redux/actions/ruleEngineAction";
import { componentStyle } from "../../config/appTheme";
import {
  vlcCardStyles,
  vlcComponentStyle,
} from "../../components/customComponentsV2/vlc/config/vlcConfig";
import { Search } from "@mui/icons-material";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import CustomPaginationV2 from "../../components/customComponentsV2/common/pagination/CustomPaginationV2";
import VlcCardHeader from "../../components/customComponentsV2/vlc/cards/VlcCardHeader";
import VlcCardContent from "../../components/customComponentsV2/vlc/cards/VlcCardContent";
import EditDetailsDialog from "../../components/customComponentsV2/vlc/pages/EditDetailsDialog";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomSelectV2 from "../../components/customComponentsV2/common/select/CustomSelectV2";
import MultiselectConnectorV2 from "../../components/customComponentsV2/common/connectors/MultiselectConnectorV2";
import CustomTabsV2 from "../../components/customComponentsV2/common/tabs/CustomTabsV2";
import DetailsLogTab from "../../components/customComponentsV2/vlc/pages/DetailsLogTab";
import CustomToolTip from "../../components/infoButton/CustomToolTip";
import VlcEEComponentAddDialog from "./VlcEEComponentAddDialog";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import CustomCheckBoxV2 from "../../components/customComponentsV2/common/checkBox/CustomCheckBoxV2";
import RequestsAndLogsTabs from "./requestAccess";
import CommonInputSwitch from "../../components/customComponentsV2/common/InputSwitch/CommonInputSwitch";

import RecreateEEDialog from "../../components/customComponentsV2/vlc/pages/RecreateEEDialog";
import DecisionMatrixPanel from "../legal-requirements/RuleCenter/Executions/DecisionMatrixPanel";
import { showToastMessage } from "../../redux/actions/actionHandler";
import PropTypes from "prop-types";
import AttributesForm from "./Attributes/AttributesForm";
import AttributeUpdateDialog from "./Attributes/AttributeUpdateDialog";
import SelectVehicleComponentForVehicleSystem from "./SelectVehicleComponentForVehicleSystem";
import { archiveReasons, PROJECT_STATUS } from "../../config/appConstants";

const isAttributeDecisionMatrixContextValid = (data) => {
  if (!data?.standards || data.standards.length === 0) {
    return false;
  }

  return data.standards.some((standard) => {
    if (!standard.rule_sets || standard.rule_sets.length === 0) {
      return false;
    }

    return standard.rule_sets.some(
      (ruleSet) => ruleSet.workflows && ruleSet.workflows.length > 0,
    );
  });
};

const resolveSelectedEEComponent = (eEComponentsList, selectedEEIdFromUrl, currentSelected) => {
  const list = Array.isArray(eEComponentsList) ? eEComponentsList : [];
  if (!list.length) return null;

  if (selectedEEIdFromUrl) {
    const found = list.find((ee) => Number(ee.ee_id) === Number(selectedEEIdFromUrl));
    if (found) return found;
  }

  if (currentSelected) {
    const matched = list.find((ee) => ee.ee_id === currentSelected.ee_id);
    if (matched) return matched;
  }

  return list[0] || null;
};

const buildUrlWithoutParams = (pathname, searchString, paramsToRemove = []) => {
  const params = new URLSearchParams(searchString);
  for (const param of paramsToRemove) {
    params.delete(param);
  }
  return `${pathname}?${params.toString()}`;
};

function checkIsSWMFilterActive({
  swmNameFilter,
  swmVersionFilter,
  swmNameFilterSearch,
  swmVersionFilterSearch,
  swmSortField,
  swmSortOrder,
}) {
  return (
    Boolean(swmNameFilter?.length) ||
    Boolean(swmVersionFilter?.length) ||
    Boolean(swmNameFilterSearch?.length) ||
    Boolean(swmVersionFilterSearch?.length) ||
    (Boolean(swmSortField) && swmSortField !== 'name') ||
    (Boolean(swmSortOrder) && swmSortOrder !== 'asc')
  );
}

function VlcEEComponentCardMenu({
  item,
  canEdit,
  viewArchived,
  anchorEl,
  activeItemId,
  handleCloseMenu,
  handleMenuAction,
  handleSelectedEEComponentReplaceDetails,
  setOpenEditDetailsDialog,
  setSelectedEEItem,
  selectedProjectDetails,
}) {
  const reduxProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const projectDetails = selectedProjectDetails || reduxProjectDetails;
  const isOpen = Boolean(anchorEl) && activeItemId === item.ee_id;

  const handleEditDetailsClick = () => {
    if (viewArchived) {
      handleCloseMenu();
      handleSelectedEEComponentReplaceDetails(item);
    } else {
      setOpenEditDetailsDialog(true);
      setSelectedEEItem(item);
    }
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={isOpen}
      onClose={handleCloseMenu}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 0,
            boxShadow: 1,
          },
        },
      }}
    >
      <MenuItem
        onClick={() => handleMenuAction('view', item)}
        sx={{ ...componentStyle.font.cardText }}
      >
        View
      </MenuItem>

      {canEdit && item?.action_type === 0 && (
        <MenuItem
          onClick={handleEditDetailsClick}
          disabled={!item?.status}
          sx={{ ...componentStyle.font.cardText }}
        >
          Edit Details
        </MenuItem>
      )}

      {!canEdit && (
        <MenuItem
          onClick={() => handleMenuAction('requests', item)}
          sx={{ ...componentStyle.font.cardText }}
        >
          Edit Request
        </MenuItem>
      )}

      <MenuItem
        onClick={() => handleMenuAction('logs', item)}
        sx={{ ...componentStyle.font.cardText }}
      >
        Log
      </MenuItem>

      {item?.archive_status?.toUpperCase() !== 'ARCHIVED' &&
        projectDetails?.status === PROJECT_STATUS.DRAFT &&
        Boolean(projectDetails?.archive_vlc) && (
        <MenuItem
          onClick={() => handleMenuAction('archive', item)}
          sx={{ ...componentStyle.font.cardText }}
        >
          Archive
        </MenuItem>
      )}

      {item?.archive_status?.toUpperCase() === 'ARCHIVED' &&
        projectDetails?.status === PROJECT_STATUS.DRAFT &&
        Boolean(projectDetails?.archive_vlc) && (
        <MenuItem
          onClick={() => handleMenuAction('retrieve', item)}
          sx={{ ...componentStyle.font.cardText }}
        >
          Retrieve
        </MenuItem>
      )}
    </Menu>
  );
}

VlcEEComponentCardMenu.propTypes = {
  item: PropTypes.object,
  canEdit: PropTypes.bool,
  viewArchived: PropTypes.bool,
  anchorEl: PropTypes.any,
  activeItemId: PropTypes.any,
  handleCloseMenu: PropTypes.func,
  handleMenuAction: PropTypes.func,
  handleSelectedEEComponentReplaceDetails: PropTypes.func,
  setOpenEditDetailsDialog: PropTypes.func,
  setSelectedEEItem: PropTypes.func,
  selectedProjectDetails: PropTypes.object,
};

const checkIsVariantArchived = (selectedSingleVariantDetails, item) => {
  const isStatusArchived = (val) =>
    typeof val === "string" && val.toUpperCase() === "ARCHIVED";

  if (
    isStatusArchived(selectedSingleVariantDetails?.archive_status) ||
    isStatusArchived(selectedSingleVariantDetails?.archive_reason) ||
    isStatusArchived(selectedSingleVariantDetails?.status) ||
    isStatusArchived(item?.variant_details?.archive_status) ||
    isStatusArchived(item?.variant_archive_status)
  ) {
    return true;
  }

  if (
    Array.isArray(selectedSingleVariantDetails?.archive_reason) &&
    selectedSingleVariantDetails.archive_reason.some(
      (r) =>
        typeof r === "string" &&
        (r.toUpperCase() === "ARCHIVED" || r.toUpperCase() === "VARIANT_ARCHIVED"),
    )
  ) {
    return true;
  }

  if (
    Array.isArray(item?.archive_reason) &&
    item.archive_reason.some((r) => {
      const str = String(r).toUpperCase();
      return str.includes("VARIANT") || str === "ARCHIVED";
    })
  ) {
    return true;
  }

  return false;
};

const checkIsParentVCArchived = (item) => {
  const isVcArchivedStatus = (val) =>
    typeof val === "string" && ["ARCHIVED", "ARCHIVE"].includes(val.toUpperCase());

  if (isVcArchivedStatus(item?.vehicle_component_details?.[0]?.archive_status)) {
    return true;
  }

  if (
    Array.isArray(item?.vehicle_component_details) &&
    item.vehicle_component_details.some((vc) => isVcArchivedStatus(vc?.archive_status))
  ) {
    return true;
  }

  return isVcArchivedStatus(item?.vehicle_component_details?.archive_status);
};

const computeArchiveDisplayReasons = (missingArchiveReasons, isVariantArchived, isParentVCArchived) => {
  const displayMissingReasons = missingArchiveReasons.filter(
    (r) => !String(r).toUpperCase().includes("VARIANT") && String(r).toUpperCase() !== "ARCHIVED",
  );
  if (displayMissingReasons.length > 0) {
    return displayMissingReasons;
  }
  if (!isVariantArchived && !isParentVCArchived && missingArchiveReasons.length > 0) {
    return missingArchiveReasons;
  }
  return [];
};

const buildArchiveTooltipTitle = (displayReasons, isVariantArchived, isParentVCArchived) =>
  [
    displayReasons.length > 0 ? `Missing ${displayReasons.join(", ")}` : "",
    isVariantArchived ? "Varient is not active" : "",
    isParentVCArchived ? "Parent Vehicle Component is not active" : "",
  ]
    .filter(Boolean)
    .join(", ");

function VlcEEComponentCardItem({
  item,
  selectedEEComponentDetails,
  handleSelectedEEComponent,
  viewArchived,
  selectedArchivedEEs,
  setSelectedArchivedEEs,
  selectedArchivedSWMs,
  clearAllCounter,
  setOpenDialogDraftToAdd,
  variantOptionList,
  selectedSingleVariantDetails,
  handleActionType1DialogOpen,
  handleActionType2DialogOpen,
  setSelectedEEComponentUpdateDetails,
  setResumeAttributeImpactFlowTrigger,
  setOpenEEComponentUpdateDialog,
  setResumeVersionImpactFlowTrigger,
  handleLockUnLockEE,
  handleMoreClick,
  anchorEl,
  activeItemId,
  handleCloseMenu,
  selectedProjectDetails,
  handleMenuAction,
  handleSelectedEEComponentReplaceDetails,
  setOpenEditDetailsDialog,
  setSelectedEEItem,
}) {
  const isSelected = selectedEEComponentDetails?.ee_id === item?.ee_id;
  const isArchived = item?.archive_status?.toUpperCase() === 'ARCHIVED';
  const hasMissingArchiveReasons = Array.isArray(item?.archive_reason) &&
    item.archive_reason.some((r) => !archiveReasons.ee_exceptionToActive.includes(r));
  const missingArchiveReasons = hasMissingArchiveReasons
    ? item.archive_reason.filter((r) => !archiveReasons.ee_exceptionToActive.includes(r))
    : [];

  const isVariantArchived = checkIsVariantArchived(selectedSingleVariantDetails, item);
  const isParentVCArchived = checkIsParentVCArchived(item);
  const displayReasons = computeArchiveDisplayReasons(
    missingArchiveReasons,
    isVariantArchived,
    isParentVCArchived,
  );
  const tooltipTitle = buildArchiveTooltipTitle(
    displayReasons,
    isVariantArchived,
    isParentVCArchived,
  );
  const showActionIcon = [1, 2, 4, 5].includes(item.action_type);
  const canEdit = !item?.is_locked && (item.is_edit_enabled || selectedProjectDetails?.is_user_project_owner);
  const assemblyNo = item?.assembly_number || '';
  const versionNo = item?.version || '';
  const descriptionText = item?.description || '';

  const getActionTypeTitle = (actionType) => {
    if (actionType === 1) return 'Action required in EE Component to EE Component linking';
    if (actionType === 2) return 'Action required in EE Component to Legal requirements';
    if (actionType === 4) return 'Action required in EE Component Attribute';
    return 'Action pending in EE Component Version';
  };

  const handleActionIconClick = () => {
    if (item.action_type === 1) {
      handleActionType1DialogOpen(item);
    } else if (item.action_type === 2) {
      handleActionType2DialogOpen(item);
    } else if (item.action_type === 4) {
      setSelectedEEComponentUpdateDetails(item);
      setResumeAttributeImpactFlowTrigger((prev) => prev + 1);
    } else {
      setSelectedEEComponentUpdateDetails(item);
      setOpenEEComponentUpdateDialog(true);
      setResumeVersionImpactFlowTrigger((prev) => prev + 1);
    }
  };

  const handleCheckboxChange = (e) => {
    if (e.target.checked) {
      setSelectedArchivedEEs((prev) => [...(prev || []), item.ee_id]);
    } else {
      setSelectedArchivedEEs((prev) => (prev || []).filter((id) => id !== item.ee_id));
    }
  };

  return (
    <VlcCardContent
      onClick={handleSelectedEEComponent}
      data={item}
      key={item.ee_id}
      isSelected={isSelected}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'stretch',
          cursor: 'pointer',
        }}
      >
        {isArchived && (
          <Box
            sx={{
              p: 1,
              pb: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <CustomCheckBoxV2
              noPaddingRequired={true}
              key={'ee-' + item.ee_id + '-' + clearAllCounter}
              value={selectedArchivedEEs?.includes(item.ee_id) || false}
              disabled={selectedArchivedSWMs?.length > 0}
              onChange={handleCheckboxChange}
            />
          </Box>
        )}
        <Box sx={{ width: '70%' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              p: 1,
            }}
          >
            <Typography
              sx={{
                ...componentStyle.font.text,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              <CustomToolTip
                type="string"
                length={50}
                style={{
                  ...componentStyle.font.text,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                  fontWeight: 'bold',
                }}
                title={'Assembly No: ' + assemblyNo}
              >
                {'Assembly No: ' + assemblyNo}
              </CustomToolTip>
            </Typography>

            <Typography
              sx={{
                ...componentStyle.font.text,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                color: vlcCardStyles.vlcCardListColor,
              }}
            >
              <CustomToolTip
                type="string"
                length={60}
                style={{
                  ...componentStyle.font.text,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
                title={'Version: ' + versionNo}
              >
                {'Version: ' + versionNo}
              </CustomToolTip>
            </Typography>

            <Typography
              sx={{
                ...componentStyle.font.text,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                color: vlcCardStyles.vlcCardListColor,
              }}
            >
              <CustomToolTip
                type="string"
                length={60}
                style={{
                  ...componentStyle.font.text,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
                title={'Description: ' + descriptionText}
              >
                {'Description: ' + descriptionText}
              </CustomToolTip>
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              ml: 0.5,
              mt: -0.5,
            }}
          >
            {item?.status === false && (
              <Box sx={{ pl: 0.5, pb: 0.5 }}>
                <Chip
                  onClick={() => setOpenDialogDraftToAdd(true)}
                  sx={{
                    borderRadius: 0,
                    color: '#007CB0',
                    borderColor: '#007CB0',
                    borderWidth: 1,
                    fontSize: '11px',
                  }}
                  size="small"
                  variant="outlined"
                  label={'Draft'}
                />
              </Box>
            )}
          </Box>

          {item?.variant_ids?.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                borderRadius: 0,
                ml: 0.5,
                mb: 0.5,
              }}
            >
              {variantOptionList
                ?.filter(
                  (variant) =>
                    variant?.value !== selectedSingleVariantDetails?.variant_id &&
                    item?.variant_ids?.includes(variant?.value),
                )
                ?.map((colour) => (
                  <Box key={colour?.value || colour?.label} sx={{ borderRadius: 0 }}>
                    <CustomToolTip
                      type="component"
                      title={colour?.label}
                    >
                      <Skeleton
                        animation={'false'}
                        variant="rectangular"
                        sx={{
                          backgroundColor: colour?.color,
                          marginTop: '2px',
                          marginLeft: '2px',
                        }}
                        width={12}
                        height={12}
                      />
                    </CustomToolTip>
                  </Box>
                ))}
            </Box>
          )}
        </Box>
        <Box sx={{ position: 'relative' }}>
          <Box
            key={item?.ee_id}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
            >
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  flexWrap: 'nowrap',
                  minWidth: 0,
                  overflow: 'hidden',
                }}
              >
                {(hasMissingArchiveReasons || isVariantArchived || isParentVCArchived) && (
                  <CustomToolTip
                    type="component"
                    title={tooltipTitle}
                  >
                    <ErrorOutlineIcon
                      sx={{ fontSize: '18px', color: 'red' }}
                    />
                  </CustomToolTip>
                )}
                <Box
                  width="100%"
                  display="flex"
                  justifyContent="flex-end"
                >
                  {showActionIcon && (
                    <CustomToolTip
                      type="component"
                      title={getActionTypeTitle(item.action_type)}
                    >
                      <IconButton
                        size="small"
                        sx={{ color: 'black' }}
                        onClick={handleActionIconClick}
                      >
                        <ErrorOutlineIcon
                          sx={{ fontSize: '18px' }}
                        />
                      </IconButton>
                    </CustomToolTip>
                  )}
                </Box>
                {item?.is_locked ? (
                  <CustomToolTip type="component" title="Lock">
                    <IconButton
                      size="small"
                      sx={{ color: 'black' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLockUnLockEE({
                          eeId: item?.ee_id,
                          isLocked: false,
                        });
                      }}
                    >
                      <LockIcon sx={{ fontSize: '18px' }} />
                    </IconButton>
                  </CustomToolTip>
                ) : (
                  <CustomToolTip
                    type="component"
                    title="UnLock"
                  >
                    <IconButton
                      size="small"
                      sx={{ color: 'black' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLockUnLockEE({
                          eeId: item?.ee_id,
                          isLocked: true,
                        });
                      }}
                    >
                      <NoEncryptionGmailerrorredIcon
                        sx={{ fontSize: '18px' }}
                      />
                    </IconButton>
                  </CustomToolTip>
                )}

                <CustomToolTip type="component" title="More">
                  <IconButton
                    size="small"
                    sx={{ color: 'black' }}
                    onClick={(e) => handleMoreClick(e, item)}
                  >
                    <MoreVertIcon sx={{ fontSize: '18px' }} />
                  </IconButton>
                </CustomToolTip>
              </Box>
            </Box>

            <VlcEEComponentCardMenu
              item={item}
              canEdit={canEdit}
              viewArchived={viewArchived}
              anchorEl={anchorEl}
              activeItemId={activeItemId}
              handleCloseMenu={handleCloseMenu}
              handleMenuAction={handleMenuAction}
              handleSelectedEEComponentReplaceDetails={handleSelectedEEComponentReplaceDetails}
              setOpenEditDetailsDialog={setOpenEditDetailsDialog}
              setSelectedEEItem={setSelectedEEItem}
              selectedProjectDetails={selectedProjectDetails}
            />
          </Box>
        </Box>
      </Box>
    </VlcCardContent>
  );
}

VlcEEComponentCardItem.propTypes = {
  item: PropTypes.object,
  selectedEEComponentDetails: PropTypes.object,
  handleSelectedEEComponent: PropTypes.func,
  viewArchived: PropTypes.bool,
  selectedArchivedEEs: PropTypes.arrayOf(PropTypes.any),
  setSelectedArchivedEEs: PropTypes.func,
  selectedArchivedSWMs: PropTypes.arrayOf(PropTypes.any),
  clearAllCounter: PropTypes.number,
  setOpenDialogDraftToAdd: PropTypes.func,
  variantOptionList: PropTypes.array,
  selectedSingleVariantDetails: PropTypes.object,
  handleActionType1DialogOpen: PropTypes.func,
  handleActionType2DialogOpen: PropTypes.func,
  setSelectedEEComponentUpdateDetails: PropTypes.func,
  setResumeAttributeImpactFlowTrigger: PropTypes.func,
  setOpenEEComponentUpdateDialog: PropTypes.func,
  setResumeVersionImpactFlowTrigger: PropTypes.func,
  handleLockUnLockEE: PropTypes.func,
  handleMoreClick: PropTypes.func,
  anchorEl: PropTypes.any,
  activeItemId: PropTypes.any,
  handleCloseMenu: PropTypes.func,
  selectedProjectDetails: PropTypes.object,
  handleMenuAction: PropTypes.func,
  handleSelectedEEComponentReplaceDetails: PropTypes.func,
  setOpenEditDetailsDialog: PropTypes.func,
  setSelectedEEItem: PropTypes.func,
};

function VlcEEComponentsCardHeaderTitle({
  viewArchived,
  isArchived,
  handleArchiveToggle,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  handleEERecreateOpen,
  assemblySearch,
  setAssemblySearch,
  supplier,
  selectedVariantsMany,
  variantOptionList,
  setOpenEEComponentAddDialog,
}) {
  const showRecreateIcon = Boolean(
    selectedProjectDetails?.parent_project_id &&
      selectedSingleVariantDetails?.parent_variant_details?.variant_id
  );
  const isAddDisabled = !selectedVariantsMany || !variantOptionList?.length;

  return (
    <VlcCardHeader
      title={'EE Components'}
      titleAction={
        viewArchived ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                color: '#ffffff',
                fontFamily: 'Open Sans',
                userSelect: 'none',
              }}
            >
              {isArchived ? 'Archived' : 'Active'}
            </Typography>
            <CommonInputSwitch
              checked={isArchived}
              onChange={handleArchiveToggle}
            />
          </Box>
        ) : null
      }
      showRecreateIcon={showRecreateIcon}
      onRecreateClick={handleEERecreateOpen}
    >
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        gap={1}
        sx={{
          width: '100%',
          mt: 1,
        }}
      >
        <TextField
          size="small"
          placeholder="Search Assembly Number"
          variant="outlined"
          fullWidth
          sx={componentStyle.searchField.textField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          value={assemblySearch}
          onChange={(e) => setAssemblySearch(e.target.value)}
        />
        <Box>
          {!supplier && !viewArchived && (
            <AddButtonV2
              disabled={isAddDisabled}
              handleOnClick={() => setOpenEEComponentAddDialog(true)}
            />
          )}
        </Box>
      </Box>
    </VlcCardHeader>
  );
}

VlcEEComponentsCardHeaderTitle.propTypes = {
  viewArchived: PropTypes.bool,
  isArchived: PropTypes.bool,
  handleArchiveToggle: PropTypes.func,
  selectedProjectDetails: PropTypes.shape({
    parent_project_id: PropTypes.any,
  }),
  selectedSingleVariantDetails: PropTypes.shape({
    parent_variant_details: PropTypes.shape({
      variant_id: PropTypes.any,
    }),
  }),
  handleEERecreateOpen: PropTypes.func,
  assemblySearch: PropTypes.string,
  setAssemblySearch: PropTypes.func,
  supplier: PropTypes.any,
  selectedVariantsMany: PropTypes.any,
  variantOptionList: PropTypes.arrayOf(PropTypes.any),
  setOpenEEComponentAddDialog: PropTypes.func,
};

function VlcEEComponentsDecisionMatrixDialog({
  showAttributeDecisionMatrix,
  attributeDecisionMatrixContext,
  handleAttributeDecisionMatrixClose,
  handleAttributeExecuteWorkflow,
  handleAttributeViewExecution,
  handleAttributeDecisionMatrixAction,
  handelGetAttributeSimilerTickets,
  attributeSimilarTickets,
  setAttributeSimilarTickets,
  attributeSelectedEntity,
  handelGetAttributeAiSuggestion,
  attributeSelectedTicket,
  setAttributeSelectedTicket,
  attributeOpenAiPanel,
  setAttributeOpenAiPanel,
  setAttributeCreateTicketData,
  attributeDisabledStandards,
}) {
  return (
    <Dialog
      open={showAttributeDecisionMatrix}
      onClose={handleAttributeDecisionMatrixClose}
      fullScreen
      slotProps={{
        paper: {
          sx: {
            width: '100vw',
            height: '100vh',
            margin: 0,
            borderRadius: 0,
          },
        },
      }}
    >
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: '#26890D',
        }}
      >
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Decision Matrix
          </Typography>
          <IconButton
            color="inherit"
            onClick={handleAttributeDecisionMatrixClose}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          flex: 1,
          height: 'calc(100vh - 64px)',
          overflow: 'auto',
          p: 2,
        }}
      >
        {showAttributeDecisionMatrix && (
          <DecisionMatrixPanel
            context={attributeDecisionMatrixContext}
            key={showAttributeDecisionMatrix ? 'open' : 'closed'}
            onExecuteWorkflow={handleAttributeExecuteWorkflow}
            onViewExecution={handleAttributeViewExecution}
            onClose={handleAttributeDecisionMatrixClose}
            onDecisionMatrixAction={handleAttributeDecisionMatrixAction}
            handelGetSimilerTickets={handelGetAttributeSimilerTickets}
            similarTickets={attributeSimilarTickets}
            setSimilarTickets={setAttributeSimilarTickets}
            entityDetails={attributeSelectedEntity}
            handelGetAiSuggestion={handelGetAttributeAiSuggestion}
            selectedTicket={attributeSelectedTicket}
            setSelectedTicket={setAttributeSelectedTicket}
            openAiPanel={attributeOpenAiPanel}
            setOpenAiPanel={setAttributeOpenAiPanel}
            setCreateTicketData={setAttributeCreateTicketData}
            disabledStandards={attributeDisabledStandards}
          />
        )}
      </Box>
    </Dialog>
  );
}

VlcEEComponentsDecisionMatrixDialog.propTypes = {
  showAttributeDecisionMatrix: PropTypes.bool,
  attributeDecisionMatrixContext: PropTypes.any,
  handleAttributeDecisionMatrixClose: PropTypes.func,
  handleAttributeExecuteWorkflow: PropTypes.func,
  handleAttributeViewExecution: PropTypes.func,
  handleAttributeDecisionMatrixAction: PropTypes.func,
  handelGetAttributeSimilerTickets: PropTypes.func,
  attributeSimilarTickets: PropTypes.any,
  setAttributeSimilarTickets: PropTypes.func,
  attributeSelectedEntity: PropTypes.any,
  handelGetAttributeAiSuggestion: PropTypes.func,
  attributeSelectedTicket: PropTypes.any,
  setAttributeSelectedTicket: PropTypes.func,
  attributeOpenAiPanel: PropTypes.bool,
  setAttributeOpenAiPanel: PropTypes.func,
  setAttributeCreateTicketData: PropTypes.func,
  attributeDisabledStandards: PropTypes.any,
};

function VlcEEComponents({
  getZoneEECountList,
  variantOptionList,
  heading,
  selectedSingleVariantDetails,
  eEComponentsList,
  handleEEComponentAdd,
  handleEEComponentUpdate,
  handleEEComponentReplace,
  selectedProjectDetails,
  selectedZone,
  selectedZoneDetails,
  setIsLinkSoftwareModulesScreen,
  getEEComponentsList,
  searchFieldValue,
  setSearchFieldValue,
  selectedSearchField,
  setSelectedSearchField,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  hasMore,
  setHasMore,
  swmPage,
  setSwmPage,
  swmRowsPerPage,
  setSwmRowsPerPage,
  swmHasMore,
  setSwmHasMore,
  swmNameFilter,
  setSWMNameFilter,
  swmVersionFilter,
  setSWMVersionFilter,
  swmNameFilterSearch,
  setSWMNameFilterSearch,
  swmVersionFilterSearch,
  setSWMVersionFilterSearch,
  swmSortField,
  setSWMSortField,
  swmSortOrder,
  setSWMSortOrder,
  swmListToFilter,
  filteredSWMList,
  sortedSWMList,
  selectedEEComponentDetails,
  setSelectedEEComponentDetails,
  totalEECount,
  setTotalEECount,
  totalSWMCount,
  setTotalSWMCount,
  viewArchived,
  selectedArchivedEEs,
  setSelectedArchivedEEs,
  selectedArchivedSWMs,
  setSelectedArchivedSWMs,
  clearAllCounter,
  retrievalRefreshCounter,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const selectedEEIdFromUrl = queryParams.get("ee_id");
  const selectedSWMIdFromUrl = queryParams.get("swm_id");
  const actionModeFromUrl = queryParams.get("actionMode");
  const isRedirectedEEAction =
    actionModeFromUrl === "eeComponentAction" && Boolean(selectedEEIdFromUrl);
  const isRedirectedSWMAction =
    actionModeFromUrl === "swmAction" && Boolean(selectedSWMIdFromUrl);

  const { swModulesList } = useSelector(
    (state) => state.labCarReducer,
    shallowEqual,
  );
  const adminSettingsDetails = useSelector(
    (state) => state.adminSettingsReducer,
    shallowEqual,
  );

  const profileDetails = useSelector((store) => store.profileReducer);

  const roleType = profileDetails?.profile?.role_details?.role_type;

  const supplier = roleType === 2;

  const [activeItemId, setActiveItemId] = useState(null); // State to track which item's actions are visible
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");

  const [isArchived, setIsArchived] = useState(Boolean(viewArchived));

  useEffect(() => {
    setIsArchived(Boolean(viewArchived));
  }, [viewArchived]);

  const handleArchiveToggle = (checked) => {
    const nextVal = typeof checked === "boolean" ? checked : !isArchived;
    setIsArchived(nextVal);
    getEEComponentsList({ archive: nextVal });
  };

  const [openEEComponentAddDialog, setOpenEEComponentAddDialog] =
    useState(false);
  const [copyEEComponentDetails, setCopyEEComponentDetails] = useState(null);
  const [openEEComponentUpdateDialog, setOpenEEComponentUpdateDialog] =
    useState(false);
  const [openEEComponentReplaceDialog, setOpenEEComponentReplaceDialog] =
    useState(false);
  const [openEEComponentChangeLogsDialog, setOpenEEComponentChangeLogsDialog] =
    useState(false);
  const [openRequestsDialog, setOpenRequestsDialog] = useState(false);
  const [openAttributeUpdateDialog, setOpenAttributeUpdateDialog] =
    useState(false);

  const attributeUpdateDialogRef = useRef(null);

  const [isVersionImpactFlow, setIsVersionImpactFlow] = useState(false);
  const [
    resumeAttributeImpactFlowTrigger,
    setResumeAttributeImpactFlowTrigger,
  ] = useState(0);
  const [resumeVersionImpactFlowTrigger, setResumeVersionImpactFlowTrigger] =
    useState(0);

  const [openRecreationEEDialog, setOpenRecreationEEDialog] = useState(false);
  const [recreationParentEEList, setRecreationParentEEList] = useState([]);
  const [selectedParentEEDetails, setSelectedParentEEDetails] = useState(null);

  const [isEEComponentSelected, setIsEEComponentSelected] = useState(false);

  const [
    selectedEEComponentUpdateDetails,
    setSelectedEEComponentUpdateDetails,
  ] = useState(null);
  const [
    selectedEEComponentReplaceDetails,
    setSelectedEEComponentReplaceDetails,
  ] = useState(null);

  const [eESWModulesList, setEESWModulesList] = useState([]);


  const [vsOptionsList, setVSOptionsList] = useState([]);
  const [supplierOptionsList, setSupplierOptionsList] = useState([]);

  const [eeChangeLogsList, setEEChangeLogsList] = useState([]);
  const [eeChangeLogsTotalCount, setEEChangeLogsTotalCount] = useState(0);
  const [eeLogsDialogData, setEELogsDialogData] = useState({});
  const [confirmArchiveDialogOpen, setConfirmArchiveDialogOpen] =
    useState(false);
  const [confirmRetrievalDialogOpen, setConfirmRetrievalDialogOpen] =
    useState(false);
  const [itemToArchive, setItemToArchive] = useState(null);
  const [itemToRetrieve, setItemToRetrieve] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);

  const [requestsData, setRequestsData] = useState([]);

  const [selectedECU, setSelectedECU] = useState(null);
  const [actionType1DialogOpen, setActionType1DialogOpen] = useState(false);
  const [actionType2DialogOpen, setActionType2DialogOpen] = useState(false);
  const [selectedVariantsMany, setSelectedVariantsMany] = useState([]);

  const [openDialogDraftToAdd, setOpenDialogDraftToAdd] = useState(false);
  const [openLockUnLockEEDialog, setOpenLockUnLockEEDialog] = useState(null);
  const [lockUnLockEEReason, setLockUnLockEEReason] = useState(null);
  const [lockUnLockEEData, setLockUnLockEEData] = useState(null);
  const [swmSelectedSearchField, setSWMSelectedSearchField] = useState("name");
  const [swmSearchFieldValue, setSWMSearchFieldValue] = useState("");

  const [openEditDetailsDialog, setOpenEditDetailsDialog] = useState(false);
  const [selectedEEItem, setSelectedEEItem] = useState(null);
  const [eeDetailsDialogTabs, setEeDetailsDialogTabs] = useState([
    "Details",
    "Log",
    "Attribute",
  ]);
  const [attributeDecisionMatrixContext, setAttributeDecisionMatrixContext] =
    useState(null);
  const [showAttributeDecisionMatrix, setShowAttributeDecisionMatrix] =
    useState(false);
  const [attributeExecutionId, setAttributeExecutionId] = useState(null);
  const [attributeSimilarTickets, setAttributeSimilarTickets] = useState([]);
  const [attributeSelectedEntity, setAttributeSelectedEntity] = useState(null);
  const [attributeSelectedTicket, setAttributeSelectedTicket] = useState(null);
  const [attributeOpenAiPanel, setAttributeOpenAiPanel] = useState(false);
  const [attributeCreateTicketData, setAttributeCreateTicketData] = useState(
    {},
  );
  const [attributeDecisionMatrixReason, setAttributeDecisionMatrixReason] =
    useState("");
  const [attributeDisabledStandards, setAttributeDisabledStandards] = useState(
    [],
  );

  useEffect(() => {
    if (adminSettingsDetails?.adminSettings?.data?.ecu_suppliers?.length > 0) {
      const supplierDetails =
        adminSettingsDetails?.adminSettings?.data?.ecu_suppliers?.map(
          (data) => ({
            value: data.name,
            label: data.name,
          }),
        );
      setSupplierOptionsList(supplierDetails);
    }
  }, [adminSettingsDetails]);
  useEffect(() => {
    dispatch(HandleApiActions({ ...adminSettingsAPI.get_admin_settings }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelectedSearchField("supplier_name");
    setSearchFieldValue("");
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectDetails, selectedSingleVariantDetails]);

  const handlePageChange = (selectedPage) => {
    setPage(selectedPage);
  };

  const handleLockUnLockEE = (params) => {
    setLockUnLockEEData(params);

    setOpenLockUnLockEEDialog(true);
  };
  const handleConfirmLockUnLockEE = async () => {
    let apiDetails = labCar.lock_unlock_ee_component;

    if (
      selectedProjectDetails?.project_id &&
      selectedSingleVariantDetails?.variant_id
    ) {
      customConsole({
        ...apiDetails,
        params: {
          project_id: Number(selectedProjectDetails?.project_id),
          variant_id: Number(selectedSingleVariantDetails?.variant_id),
          ee_id: Number(lockUnLockEEData?.eeId),
          is_locked: lockUnLockEEData?.isLocked,
          ...(lockUnLockEEReason && { reason: lockUnLockEEReason }),
        },
        show_toast: true,
      });

      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...apiDetails,
            params: {
              project_id: Number(selectedProjectDetails?.project_id),
              variant_id: Number(selectedSingleVariantDetails?.variant_id),
              ee_id: Number(lockUnLockEEData?.eeId),
              is_locked: lockUnLockEEData?.isLocked,
              ...(lockUnLockEEReason && { reason: lockUnLockEEReason }),
            },
            show_toast: true,
          }),
        ),
      );
      if (!resp?.error) {
        getEEComponentsList();
        handleCloseLockUnLockEE();
      }
    }
  };
  const handleCloseLockUnLockEE = () => {
    setOpenLockUnLockEEDialog(false);

    setTimeout(() => {
      setLockUnLockEEData(null);
      setLockUnLockEEReason("");
    }, 300);
  };

  const getVehicleSystemsDetails = async () => {
    let apiDetails = adminSettingsAPI.get_vehicle_systems;

    customConsole({
      ...apiDetails,
    });

    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...apiDetails,
        }),
      ),
    );

    if (resp.error) {
      setVSOptionsList([]);
    } else {
      setVSOptionsList(resp.data);
    }
  };

  useEffect(() => {
    void getVehicleSystemsDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelectedEEComponentDetails((prev) =>
      resolveSelectedEEComponent(eEComponentsList, selectedEEIdFromUrl, prev)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eEComponentsList, selectedEEIdFromUrl]);
  useEffect(() => {

  }, [eEComponentsList]);

  useEffect(() => {
    setEESWModulesList([]);
    setSwmPage(1);
    lastSwmFetchKeyRef.current = "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEEComponentDetails]);

  const handleRedirectedSWMActionCompletion = (redirectedActionSWMId, resp) => {
    const redirectedSWMActionCompleted =
      redirectedActionSWMId &&
      resp?.data?.length === 1 &&
      ![4, 5].includes(Number(resp.data[0]?.action_type));

    if (redirectedSWMActionCompleted) {
      const paramsToClear = new URLSearchParams(location.search);
      paramsToClear.delete("ee_id");
      paramsToClear.delete("swm_id");
      paramsToClear.delete("actionMode");
      paramsToClear.delete("page");
      setSelectedEEComponentDetails(null);
      setEESWModulesList([]);
      lastSwmFetchKeyRef.current = "";
      navigate(`${location.pathname}?${paramsToClear.toString()}`, {
        replace: true,
      });
    }
  };

  const getSWModulesList = async ({ force = false } = {}) => {
    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    const eeId = selectedEEComponentDetails?.ee_id;
    const redirectedActionSWMId =
      isRedirectedSWMAction &&
        String(selectedEEComponentDetails?.ee_id) === String(selectedEEIdFromUrl)
        ? selectedSWMIdFromUrl
        : null;
    const searchKey = `${swmSelectedSearchField}|${swmSearchFieldValue || ""}`;
    const reqKey = [
      projectId,
      variantId,
      eeId,
      redirectedActionSWMId,
      actionModeFromUrl,
      searchKey,
      swmPage,
      swmRowsPerPage,
      Boolean(viewArchived),
    ].join("|");
    if (
      !force &&
      !redirectedActionSWMId &&
      lastSwmFetchKeyRef.current === reqKey
    )
      return;
    lastSwmFetchKeyRef.current = reqKey;
    if (!projectId || !variantId || !eeId) {
      setTotalSWMCount(0);
      setEESWModulesList([]);
      dispatch({
        type: actionType.GET_SW_MODULES,
        payload: { data: [], total_count: 0 },
      });
      return;
    }

    let resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...labCar.get_sw_modules,
          params: {
            project_id: projectId,
            variant_id: Number(variantId),
            ee_id: eeId,
            archive: Boolean(viewArchived),
            ...(redirectedActionSWMId && {
              swm_id: Number(redirectedActionSWMId),
            }),
            ...(swmSearchFieldValue && {
              [swmSelectedSearchField]: swmSearchFieldValue,
            }),
            page_number: swmPage,
            page_size: swmRowsPerPage,
          },
          show_toast: false,
        }),
      ),
    );
    if (resp?.error) {
      setTotalSWMCount(0);
      setEESWModulesList([]);
      dispatch({
        type: actionType.GET_SW_MODULES,
        payload: { data: [], total_count: 0 },
      });
      setSwmHasMore(false);
      return;
    }
    setTotalSWMCount(resp?.total_count || 0);
    setSwmHasMore(Boolean(resp?.data?.length >= swmRowsPerPage));
    handleRedirectedSWMActionCompletion(redirectedActionSWMId, resp);
  };

  const lastSwmFetchKeyRef = useRef("");
  useEffect(() => {
    void getSWModulesList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedEEComponentDetails,
    swmSelectedSearchField,
    swmSearchFieldValue,
    swmPage,
    swmRowsPerPage,
    viewArchived,
  ]);

  useEffect(() => {
    if (retrievalRefreshCounter > 0) {
      lastSwmFetchKeyRef.current = "";
      void getSWModulesList({ force: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retrievalRefreshCounter]);

  useEffect(() => {
    if (swModulesList) {
      setEESWModulesList(swModulesList);
    }
  }, [swModulesList]);

  useEffect(() => {
    customConsole(
      "selectedEEComponentUpdateDetails:" + selectedEEComponentUpdateDetails,
    );
    customConsole(
      "selectedEEComponentReplaceDetails:" + selectedEEComponentReplaceDetails,
    );
  }, [selectedEEComponentUpdateDetails, selectedEEComponentReplaceDetails]);

  const handleSelectedEEComponent = (eEComponent) => {
    if (eEComponent) {
      if (!isEEComponentSelected) setIsEEComponentSelected(true);
      setSelectedEEComponentDetails(eEComponent);
    }
  };

  const handleSelectedEEComponentUpdateDetails = (eEComponent) => {
    setOpenEEComponentUpdateDialog(true);

    if (eEComponent) {
      setSelectedEEComponentUpdateDetails(eEComponent);
    }
  };

  const handleSelectedEEComponentReplaceDetails = (eEComponent) => {
    setOpenEEComponentReplaceDialog(true);

    if (eEComponent) {
      setSelectedEEComponentReplaceDetails(eEComponent);
    }
  };

  const clearRedirectedEEActionParams = () => {
    if (!isRedirectedEEAction) return;

    const newUrl = buildUrlWithoutParams(location.pathname, location.search, [
      "ee_id",
      "swm_id",
      "actionMode",
      "page",
    ]);
    setSelectedEEComponentUpdateDetails(null);
    setOpenAttributeUpdateDialog(false);
    setOpenEEComponentUpdateDialog(false);
    navigate(newUrl, { replace: true });
  };

  const clearRedirectedSWMActionParams = () => {
    if (!isRedirectedSWMAction) return;

    const newUrl = buildUrlWithoutParams(location.pathname, location.search, [
      "ee_id",
      "swm_id",
      "actionMode",
      "page",
    ]);
    setSelectedEEComponentDetails(null);
    setEESWModulesList([]);
    lastSwmFetchKeyRef.current = "";
    navigate(newUrl, { replace: true });
  };

  const loadAttributeDecisionMatrixContext = async () => {
    const targetEE =
      selectedEEComponentUpdateDetails || selectedEEComponentDetails;

    if (
      !targetEE?.ee_id ||
      !selectedProjectDetails?.project_id ||
      !selectedSingleVariantDetails?.variant_id
    ) {
      return;
    }

    const standardsRes = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...ruleEngineActions.decision_matrix_get_context,
          params: {
            component_type: "EE",
            component_id: targetEE.ee_id,
            project_id: selectedProjectDetails.project_id,
            variant_id: selectedSingleVariantDetails.variant_id,
          },
          show_toast: false,
        }),
      ),
    );

    const contextData = standardsRes?.data;

    if (!isAttributeDecisionMatrixContextValid(contextData)) {
      setAttributeDecisionMatrixContext(null);
      setShowAttributeDecisionMatrix(false);
      return;
    }

    setAttributeDecisionMatrixContext(contextData);
    setAttributeDecisionMatrixReason(reason?.trim() || "");
    setShowAttributeDecisionMatrix(true);
  };

  const handleAttributeDecisionMatrixClose = () => {
    setShowAttributeDecisionMatrix(false);
    setAttributeDecisionMatrixContext(null);
    setAttributeExecutionId(null);
    setAttributeSimilarTickets([]);
    setAttributeSelectedEntity(null);
    setAttributeSelectedTicket(null);
    setAttributeOpenAiPanel(false);
    setAttributeCreateTicketData({});
    setAttributeDecisionMatrixReason("");
    setAttributeDisabledStandards([]);
  };

  const handleAttributeExecuteWorkflow = async ({ mode, payload }) => {
    if (mode === "START") {
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.wfe_execution_start,
            params: {
              workflowId: payload.workflowId,
              version: payload.version,
            },
            show_toast: false,
          }),
        ),
      );

      setAttributeExecutionId(res.executionId);
      return res;
    }

    if (mode === "COMPLETE") {
      return dispatch(
        HandleApiActions({
          ...ruleEngineActions.wfe_execution_node_complete,
          params: {
            executionId: payload.executionId,
            nodeId: payload.nodeId,
            input: payload.input,
          },
          show_toast: false,
        }),
      );
    }

    throw new Error(`Unsupported execution mode: ${mode}`);
  };

  const handleAttributeViewExecution = (executionId) => {
  };

  const handleAttributeDecisionMatrixAction = async ({ standardCode } = {}) => {
    const res = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...ruleEngineActions.re_create_ticket_v1,
          params: {
            details: attributeCreateTicketData,
            ...(attributeDecisionMatrixReason && {
              reason: attributeDecisionMatrixReason,
            }),
          },
          show_toast: true,
        }),
      ),
    );

    if (!res?.error && standardCode) {
      setAttributeDisabledStandards((prev) => [
        ...new Set([...prev, standardCode]),
      ]);
    }

    return res;
  };

  const handelGetAttributeSimilerTickets = async ({ standard_name } = {}) => {
    const targetEE =
      selectedEEComponentUpdateDetails || selectedEEComponentDetails;

    const res = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...ruleEngineActions.get_similar_tickets,
          params: {
            execution_id: attributeExecutionId,
            entity_type: "ee",
            entity_id: Number(targetEE?.ee_id),
            ...(standard_name == null ? {} : { standard_name }),
          },
          show_toast: true,
        }),
      ),
    );

    setAttributeSimilarTickets(res?.data || []);
    setAttributeCreateTicketData(res?.data || []);
    return res;
  };

  const handelGetAttributeAiSuggestion = (ticket) => {
    setAttributeSelectedEntity({
      type: "INTERSECTION",
      id: ticket?.intr_id,
      targetEntityTypes: ["ARTIFACT"],
      topK: 20,
      minConfidence: 0.6,
      description: "EE Component",
    });
    setAttributeOpenAiPanel(true);
    setAttributeSelectedTicket(ticket);
  };

  const [eeDataSelected, setEeDataSelected] = useState(null);
  const handleEESWModuleAdd = async ({
    variant_ids,
    version,
    name,
    summary,
    description,
    vf_ids,
    supplier_name,
    keywords,
    git_link,
    attributes,
  }) => {
    if (
      selectedProjectDetails?.project_id &&
      selectedSingleVariantDetails?.variant_id &&
      selectedEEComponentDetails?.ee_id
    ) {
      let currentTime = new Date().toISOString();

      await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.add_sw_module,
            params: {
              ...(supplier_name && { supplier_name }),
              save_as_draft: false,
              variant_ids,
              project_id: Number(selectedProjectDetails?.project_id),
              variant_id: Number(selectedSingleVariantDetails?.variant_id),
              ee_id: Number(selectedEEComponentDetails?.ee_id),
              version,
              ...(git_link && { git_link }),
              name,
              ...(summary && { summary }),
              description,
              keywords,
              vf_ids,
              current_time: currentTime,
              attributes: attributes,
            },
            show_toast: true,
          }),
        ),
      );
      await getSWModulesList({ force: true });

    }

    return false;
  };

  const handleEESWModuleUpdate = ({
    swm_id,
    version,
    will_it_impact,
    reason,
    github_link,
    requires_homologation,
  }) => {
    if (
      selectedProjectDetails?.project_id &&
      selectedSingleVariantDetails?.variant_id &&
      selectedEEComponentDetails?.ee_id
    ) {
      let currentTime = new Date().toISOString();

      customConsole({
        project_id: Number(selectedProjectDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedEEComponentDetails?.ee_id),
        swm_id,
        version,
        current_time: currentTime,
        reason,
        will_it_impact,
      });

      dispatch(
        HandleApiActions({
          ...labCar.update_sw_module,
          params: {
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id: Number(selectedEEComponentDetails?.ee_id),
            swm_id,
            version,
            current_time: currentTime,
            reason,
            will_it_impact,
            requires_homologation,
          },
          show_toast: true,
        }),
      ).then(() => {
        void getSWModulesList({ force: true });
      });
    }
  };

  const handleEESWModuleReplace = ({
    supplier_name,
    swm_id,
    version,
    name,
    summary,
    description,
    vf_ids,
    will_it_impact,
    reason,
  }) => {
    if (
      selectedProjectDetails?.project_id &&
      selectedSingleVariantDetails?.variant_id &&
      selectedEEComponentDetails?.ee_id
    ) {
      let currentTime = new Date().toISOString();

      customConsole({
        project_id: Number(selectedProjectDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedEEComponentDetails?.ee_id),
        swm_id,
        version,
        name,
        ...(summary && { summary }),
        description,
        vf_ids,
        current_time: currentTime,
      });

      dispatch(
        HandleApiActions({
          ...labCar.replace_sw_module,
          params: {
            supplier_name: supplier_name,
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id: Number(selectedEEComponentDetails?.ee_id),
            swm_id,
            version,
            name,
            ...(summary && { summary }),
            description,
            vf_ids,
            current_time: currentTime,
            reason,
            will_it_impact,
          },
          show_toast: true,
        }),
      ).then(() => {
        void getSWModulesList();
      });
    }
  };

  const handleGetEEComponentChangeLogs = async ({
    ee_id,
    pageNo,
    pageSize,
  }) => {
    let apiDetails = labCar.get_ee_components_change_logs;

    if (
      selectedProjectDetails?.project_id &&
      selectedSingleVariantDetails?.variant_id
    ) {
      customConsole({
        ...apiDetails,
        params: {
          project_id: Number(selectedProjectDetails?.project_id),
          varient_id: Number(selectedSingleVariantDetails?.variant_id),
          ee_id,
          page_number: pageNo,
          page_size: pageSize,
        },
        show_toast: false,
      });

      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...apiDetails,
            params: {
              project_id: Number(selectedProjectDetails?.project_id),
              variant_id: Number(selectedSingleVariantDetails?.variant_id),
              ee_id,
              page_number: pageNo,
              page_size: pageSize,
            },
            show_toast: false,
          }),
        ),
      );
      if (resp.error) {
        setEEChangeLogsList([]);
      } else {
        setEEChangeLogsList(resp?.data);
        setEEChangeLogsTotalCount(resp?.total_count);
      }
    }
  };


  const handleEELogsDialogOpen = (data) => {
    setSelectedEEItem(data);
    setOpenEEComponentChangeLogsDialog(true);


    setEELogsDialogData(data);
  };

  const handleEEViewDialogOpen = (data) => {
    setSelectedEEItem(data);
    setEELogsDialogData(data);
    setEeDetailsDialogTabs(["Details", "Attribute"]);
    setOpenEEComponentChangeLogsDialog(true);
  };

  const handleEELogsDialogClose = () => {
    setEELogsDialogData(null);
    setEEChangeLogsList([]);
    setEeDetailsDialogTabs(["Details", "Log", "Attribute"]);

    setOpenEEComponentChangeLogsDialog(false);
  };

  const handleEEComponentCopyOpen = () => {
    const eeComponentToCopy =
      selectedEEItem || eeLogsDialogData || selectedEEComponentDetails;

    if (!eeComponentToCopy) return;

    setCopyEEComponentDetails(eeComponentToCopy);
    setOpenEEComponentChangeLogsDialog(false);
    setOpenEEComponentAddDialog(true);
  };


  const handleRequestsDialogOpen = (data) => {
    setEeDataSelected(data);
    setOpenRequestsDialog(true);
    void getRequests();
  };

  const handleDialogClose = () => {
    if (dialogOpen) setDialogOpen(false);
    setReason(""); // Reset reason
  };

  const handleRequestsDialogClose = () => {
    setOpenRequestsDialog(false);
    setReason(""); // Reset reason
  };

  const handleSubmit = async () => {
    const reqObj = {
      project_id: Number(selectedProjectDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEComponentDetails?.ee_id),
      request_reason: reason,
    };

    try {
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...editRequestAPI.create_edit_requests,
            params: reqObj,
            show_toast: true,
          }),
        ),
      );
      if (!response.error) {
        void getRequests();
        setReason("");
        handleDialogClose();
      }
    } catch (error) {
      console.error("Failed to update the reason", error);
    }
  };

  const handleMoreClick = (e, item) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setActiveItemId(item.ee_id);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setActiveItemId(null);
  };

  const handleMenuAction = (action, item) => {
    handleCloseMenu(); // Close the menu after action
    if (action === "update") {
      handleSelectedEEComponentUpdateDetails(item);
    } else if (action === "replace") {
      handleSelectedEEComponentReplaceDetails(item);
    } else if (action === "logs") {
      setEeDetailsDialogTabs(["Log"]);
      handleEELogsDialogOpen(item);
    } else if (action === "requests") {
      handleRequestsDialogOpen(item);
    } else if (action === "view") {
      handleEEViewDialogOpen(item);
    } else if (action === "updateAttribute") {
      setSelectedEEComponentUpdateDetails(item);
      setOpenAttributeUpdateDialog(true);
    } else if (action === "archive") {
      setItemToArchive(item);
      setConfirmArchiveDialogOpen(true);
    } else if (action === "retrieve") {
      setItemToRetrieve(item);
      setConfirmRetrievalDialogOpen(true);
    } 
  };

  const handleConfirmRetrieveEE = async () => {
    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    const item = itemToRetrieve || itemToArchive;
    if (!projectId || !variantId || !item?.ee_id) {
      setConfirmRetrievalDialogOpen(false);
      setItemToRetrieve(null);
      return;
    }

    try {
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.retrieve_ee_components,
            params: {
              ee_ids: [item.ee_id],
              project_id: Number(projectId),
              variant_id: Number(variantId),
            },
            show_toast: true,
          }),
        ),
      );
      if (!res?.error) {
        getEEComponentsList({ archive: isArchived });
        if (typeof getZoneEECountList === "function") {
          getZoneEECountList();
        }
        if (typeof setSelectedArchivedEEs === "function") {
          setSelectedArchivedEEs((prev) => (prev || []).filter((id) => id !== item.ee_id));
        }
      }
    } catch (err) {
      console.error("Error retrieving EE component:", err);
    } finally {
      setConfirmRetrievalDialogOpen(false);
      setItemToRetrieve(null);
    }
  };

  const handleConfirmArchiveEE = async () => {
    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    const item = itemToArchive;
    if (!projectId || !variantId || !item?.ee_id) {
      setConfirmArchiveDialogOpen(false);
      setItemToArchive(null);
      return;
    }

    try {
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.archive_ee_component,
            params: {
              ee_ids: [item.ee_id],
              project_id: Number(projectId),
              variant_id: Number(variantId),
            },
            show_toast: true,
          }),
        ),
      );
      if (!res?.error) {
        getEEComponentsList({ archive: isArchived });
        if (typeof getZoneEECountList === "function") {
          getZoneEECountList();
        }
      }
    } catch (err) {
      console.error("Error archiving EE component:", err);
    } finally {
      setConfirmArchiveDialogOpen(false);
      setItemToArchive(null);
    }
  };

  const getRequests = async () => {
    let apiDetails = editRequestAPI.get_edit_requests;
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id: Number(selectedEEComponentDetails?.ee_id),
            is_sent_requests: true,
            page_number: 1,
            page_size: 25,
          },
          show_toast: false,
        }),
      ),
    );
    if (resp.error) {
      setRequestsData([]);
    } else {
      setRequestsData(resp.data);
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 100:
        return "Pending";
      case 200:
        return "Approved";
      case 300:
        return "Rejected";
      default:
        return "Unknown Status";
    }
  };



  const handleActionType1DialogOpen = (ecu) => {
    setSelectedECU(ecu);
    setActionType1DialogOpen(true);
  };

  const handleActionType2DialogOpen = (ecu) => {
    setSelectedECU(ecu);
    setActionType2DialogOpen(true);
  };

  const handleActionType1DialogClose = () => {
    if (selectedECU) setSelectedECU(null);
    setActionType1DialogOpen(false);
  };

  const handleActionType2DialogClose = () => {
    if (selectedECU) setSelectedECU(null);
    setActionType2DialogOpen(false);
  };

  const handleRedirectionToTypeApproval = () => {
    navigate(
      `/dashboard/v2/type-approval/link?ee_id=${selectedEEComponentDetails?.ee_id}&zone=${selectedZone?.zone_position_name}&tab=2`,
    );
    setActionType2DialogOpen(false);
  };

  const getParentEEDetails = async () => {
    let parentEEResponse = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...labCar.get_parent_ee,
          params: {
            project_id: selectedProjectDetails.project_id,
            parent_project_id: selectedProjectDetails.parent_project_id,
            variant_id: selectedSingleVariantDetails.variant_id,
            parent_variant_id:
              selectedSingleVariantDetails?.parent_variant_details?.variant_id,
          },
        }),
      ),
    );
    if (parentEEResponse.error) {
      showToastMessage({
        type: "error",
        message: parentEEResponse.msg,
      });
      setRecreationParentEEList([]);
      setSelectedParentEEDetails(null);
    } else {
      setSelectedParentEEDetails(parentEEResponse.data[0] || null);
      setRecreationParentEEList(parentEEResponse.data || []);
      setOpenRecreationEEDialog(true);
    }
  };

  const handleEERecreateOpen = async () => {
    await getParentEEDetails();
  };

  const handleEEComponentRecreate = () => {
  };


  const isSWMFilterActive = checkIsSWMFilterActive({
    swmNameFilter,
    swmVersionFilter,
    swmNameFilterSearch,
    swmVersionFilterSearch,
    swmSortField,
    swmSortOrder,
  });

  const [assemblySearch, setAssemblySearch] = useState("");
  const debounceRef = useRef();

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSelectedSearchField("assembly_number");
      setSearchFieldValue(assemblySearch);
    }, 500);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assemblySearch]);



  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexDirection: "row",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Card
            sx={{
              ...vlcComponentStyle.card,
              display: "flex",
              flexDirection: "column",
              height: "450px",
            }}
          >
            <CardHeader
              sx={vlcComponentStyle.cardHeader}
              title={
                <VlcEEComponentsCardHeaderTitle
                  viewArchived={viewArchived}
                  isArchived={isArchived}
                  handleArchiveToggle={handleArchiveToggle}
                  selectedProjectDetails={selectedProjectDetails}
                  selectedSingleVariantDetails={selectedSingleVariantDetails}
                  handleEERecreateOpen={handleEERecreateOpen}
                  assemblySearch={assemblySearch}
                  setAssemblySearch={setAssemblySearch}
                  supplier={supplier}
                  selectedVariantsMany={selectedVariantsMany}
                  variantOptionList={variantOptionList}
                  setOpenEEComponentAddDialog={setOpenEEComponentAddDialog}
                />
              }
            />

            <CardContent
              sx={{
                ...vlcCardStyles.CardContent,
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                p: 0,
                scrollbarColor: "#888 #f1f1f1",
                scrollbarWidth: "thin",
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#888",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#555",
                },
              }}
            >
              <Box sx={{ p: 0, m: 0 }}>
                {eEComponentsList && eEComponentsList?.length > 0 ? (
                  eEComponentsList?.map((item) => (
                    <VlcEEComponentCardItem
                      key={item.ee_id}
                      item={item}
                      selectedEEComponentDetails={selectedEEComponentDetails}
                      handleSelectedEEComponent={handleSelectedEEComponent}
                      viewArchived={viewArchived}
                      selectedArchivedEEs={selectedArchivedEEs}
                      setSelectedArchivedEEs={setSelectedArchivedEEs}
                      selectedArchivedSWMs={selectedArchivedSWMs}
                      clearAllCounter={clearAllCounter}
                      setOpenDialogDraftToAdd={setOpenDialogDraftToAdd}
                      variantOptionList={variantOptionList}
                      selectedSingleVariantDetails={selectedSingleVariantDetails}
                      handleActionType1DialogOpen={handleActionType1DialogOpen}
                      handleActionType2DialogOpen={handleActionType2DialogOpen}
                      setSelectedEEComponentUpdateDetails={setSelectedEEComponentUpdateDetails}
                      setResumeAttributeImpactFlowTrigger={setResumeAttributeImpactFlowTrigger}
                      setOpenEEComponentUpdateDialog={setOpenEEComponentUpdateDialog}
                      setResumeVersionImpactFlowTrigger={setResumeVersionImpactFlowTrigger}
                      handleLockUnLockEE={handleLockUnLockEE}
                      handleMoreClick={handleMoreClick}
                      anchorEl={anchorEl}
                      activeItemId={activeItemId}
                      handleCloseMenu={handleCloseMenu}
                      selectedProjectDetails={selectedProjectDetails}
                      handleMenuAction={handleMenuAction}
                      handleSelectedEEComponentReplaceDetails={handleSelectedEEComponentReplaceDetails}
                      setOpenEditDetailsDialog={setOpenEditDetailsDialog}
                      setSelectedEEItem={setSelectedEEItem}
                    />
                  ))
                ) : (
                  <Typography variant="body1" sx={styles.noDataAvailable}>
                    No data available
                  </Typography>
                )}
              </Box>
            </CardContent>
            {totalEECount > 0 && (
              <Paper
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  minHeight: "40px",
                  borderTop: "1px solid #ddd",
                  borderRadius: "0px !important",
                }}
              >
                <CustomPaginationV2
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={handlePageChange}
                  totalItems={totalEECount}
                />
              </Paper>
            )}
          </Card>
          <CustomDialogBoxV2
            open={openLockUnLockEEDialog}
            onClose={handleCloseLockUnLockEE}
            title={`Reason to ${
              lockUnLockEEData?.isLocked ? "Lock" : "UnLock"
            }`}
            successButtonName={"Confirm"}
            rejectButtonName={"Cancel"}
            onClickSuccess={handleConfirmLockUnLockEE}
            onClickReject={handleCloseLockUnLockEE}
            actionButtonStyle={"continuous"}
            onDisabled={lockUnLockEEReason?.length < 3}
          >
            <Stack spacing={2} sx={{ width: "350px" }}>
              <CustomFormTextFieldV2
                label="Reason"
                value={lockUnLockEEReason}
                setValue={setLockUnLockEEReason}
                required={true}
              />
            </Stack>
          </CustomDialogBoxV2>

          <EditDetailsDialog
            openEditDetails={openEditDetailsDialog}
            setOpenEditDetails={setOpenEditDetailsDialog}
            onClick={(type) => {
              setOpenEditDetailsDialog(false);
              handleMenuAction(type, selectedEEItem);
            }}
          />


          <VlcEEComponentAddDialog
            title={"Add EE Component"}
            supplierOptionsList={supplierOptionsList}
            adminSettingsDetails={adminSettingsDetails}
            selectedZone={selectedZone}
            selectedEEComponentDetails={selectedEEComponentDetails}
            selectedProjectDetails={selectedProjectDetails}
            selectedVariantsMany={selectedVariantsMany}
            setSelectedVariantsMany={setSelectedVariantsMany}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
            variantOptionList={variantOptionList}
            selectedZoneDetails={selectedZoneDetails}
            openEEComponentAddDialog={openEEComponentAddDialog}
            setOpenEEComponentAddDialog={setOpenEEComponentAddDialog}
            handleEEComponentAdd={handleEEComponentAdd}
            vsOptionsList={vsOptionsList}
            getEEComponentsList={getEEComponentsList}
            getZoneEECountList={getZoneEECountList}
            copyEEComponentDetails={copyEEComponentDetails}
            onCopyPrefillClear={() => setCopyEEComponentDetails(null)}
          />

          <VlcEEComponentUpdateDialog
            variantOptionList={variantOptionList}
            adminSettingsDetails={adminSettingsDetails}
            openEEComponentUpdateDialog={openEEComponentUpdateDialog}
            setOpenEEComponentUpdateDialog={setOpenEEComponentUpdateDialog}
            handleEEComponentUpdate={handleEEComponentUpdate}
            selectedEEComponentUpdateDetails={selectedEEComponentUpdateDetails}
            setSelectedEEComponentUpdateDetails={
              setSelectedEEComponentUpdateDetails
            }
            setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
            selectedZone={selectedZone}
            selectedEEComponentDetails={selectedEEComponentDetails}
            getEEComponentsList={getEEComponentsList}
            getSWModulesList={getSWModulesList}
            selectedProjectDetails={selectedProjectDetails}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
            isVersionImpactFlow={isVersionImpactFlow}
            setIsVersionImpactFlow={setIsVersionImpactFlow}
            resumeAttributeImpactFlowTrigger={resumeAttributeImpactFlowTrigger}
            resumeVersionImpactFlowTrigger={resumeVersionImpactFlowTrigger}
            onRedirectedActionDialogClose={clearRedirectedEEActionParams}
          />

          <AttributeUpdateDialog
            ref={attributeUpdateDialogRef}
            openAttributeUpdateDialog={openAttributeUpdateDialog}
            setOpenAttributeUpdateDialog={setOpenAttributeUpdateDialog}
            handleCloseAttributeUpdateDialog={() => {
              setOpenAttributeUpdateDialog(false);
              setIsVersionImpactFlow(false);
              clearRedirectedEEActionParams();
            }}
            onClickSuccess={() => {
              setOpenAttributeUpdateDialog(false);
              getEEComponentsList();
            }}
            onIntersectionNo={loadAttributeDecisionMatrixContext}
            onShowUpdateDialog={() => {
              setIsVersionImpactFlow(true);
              getEEComponentsList();
              setTimeout(() => {
                setOpenEEComponentUpdateDialog(true);
              }, 300);
            }}
            selectedComponent={selectedEEComponentUpdateDetails}
            componentType={"ee"}
            variantId={selectedSingleVariantDetails?.variant_id}
          />

          <RecreateEEDialog
            title={"Recreate EE Component"}
            supplierOptionsList={supplierOptionsList}
            variantOptionList={variantOptionList}
            adminSettingsDetails={adminSettingsDetails}
            openEEComponentReplaceDialog={openRecreationEEDialog}
            setOpenEEComponentReplaceDialog={setOpenRecreationEEDialog}
            handleEEComponentReplace={handleEEComponentRecreate}
            selectedEEComponentReplaceDetails={selectedParentEEDetails}
            setSelectedEEComponentReplaceDetails={
              setSelectedEEComponentReplaceDetails
            }
            getZoneEECountList={getZoneEECountList}
            setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
            selectedZone={selectedZone}
            vsOptionsList={vsOptionsList}
            selectedEEComponentDetails={selectedEEComponentDetails}
            getEEComponentsList={getEEComponentsList}
            getSWModulesList={getSWModulesList}
            selectedProjectDetails={selectedProjectDetails}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
            recreationParentEEList={recreationParentEEList}
            setRecreationParentEEList={setRecreationParentEEList}
            selectedParentEEDetails={selectedParentEEDetails}
            setSelectedParentEEDetails={setSelectedParentEEDetails}
            onClickAddForRecreate={handleEEComponentRecreate}
            getParentEEDetails={getParentEEDetails}
          />

          <VlcEEComponentReplaceDialog
            title={viewArchived ? "Draft EE Component" : "Replace EE Component"}
            supplierOptionsList={supplierOptionsList}
            variantOptionList={variantOptionList}
            adminSettingsDetails={adminSettingsDetails}
            openEEComponentReplaceDialog={openEEComponentReplaceDialog}
            setOpenEEComponentReplaceDialog={setOpenEEComponentReplaceDialog}
            handleEEComponentReplace={handleEEComponentReplace}
            selectedEEComponentReplaceDetails={
              selectedEEComponentReplaceDetails
            }
            setSelectedEEComponentReplaceDetails={
              setSelectedEEComponentReplaceDetails
            }
            getZoneEECountList={getZoneEECountList}
            setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
            selectedZone={selectedZone}
            selectedZoneDetails={selectedZoneDetails}
            vsOptionsList={vsOptionsList}
            selectedEEComponentDetails={selectedEEComponentDetails}
            getEEComponentsList={getEEComponentsList}
            getSWModulesList={getSWModulesList}
            selectedProjectDetails={selectedProjectDetails}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
          />

          <VlcEEComponentReplaceDialog
            title={"Add Draft EE Component"}
            supplierOptionsList={supplierOptionsList}
            variantOptionList={variantOptionList}
            adminSettingsDetails={adminSettingsDetails}
            openEEComponentReplaceDialog={openDialogDraftToAdd}
            setOpenEEComponentReplaceDialog={setOpenDialogDraftToAdd}
            handleEEComponentReplace={handleEEComponentReplace}
            selectedEEComponentReplaceDetails={selectedEEComponentDetails}
            setSelectedEEComponentReplaceDetails={
              setSelectedEEComponentReplaceDetails
            }
            setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
            selectedZone={selectedZone}
            selectedZoneDetails={selectedZoneDetails}
            vsOptionsList={vsOptionsList}
            selectedEEComponentDetails={selectedEEComponentDetails}
            getEEComponentsList={getEEComponentsList}
            getSWModulesList={getSWModulesList}
            selectedProjectDetails={selectedProjectDetails}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <VlcSWModules
            variantOptionList={variantOptionList}
            heading={selectedEEComponentDetails?.description}
            sWModulesList={isSWMFilterActive ? sortedSWMList : eESWModulesList}
            selectedArchivedSWMs={selectedArchivedSWMs}
            setSelectedArchivedSWMs={setSelectedArchivedSWMs}
            selectedArchivedEEs={selectedArchivedEEs}
            viewArchived={viewArchived}
            handleSWModuleAdd={handleEESWModuleAdd}
            handleSWModuleUpdate={handleEESWModuleUpdate}
            handleSWModuleReplace={handleEESWModuleReplace}
            setIsEEComponentSelected={setIsEEComponentSelected}
            setSelectedEEComponentDetails={setSelectedEEComponentDetails}
            selectedProjectDetails={selectedProjectDetails}
            selectedEEComponentDetails={selectedEEComponentDetails}
            setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
            selectedZone={selectedZone}
            getSWModulesList={getSWModulesList}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
            swmSearchFieldValue={swmSearchFieldValue}
            setSWMSearchFieldValue={setSWMSearchFieldValue}
            swmSelectedSearchField={swmSelectedSearchField}
            setSWMSelectedSearchField={setSWMSelectedSearchField}
            page={swmPage}
            setPage={setSwmPage}
            rowsPerPage={swmRowsPerPage}
            setRowsPerPage={setSwmRowsPerPage}
            totalSWMCount={totalSWMCount}
            hasMore={swmHasMore}
            setHasMore={setSwmHasMore}
            swmNameFilter={swmNameFilter}
            setSWMNameFilter={setSWMNameFilter}
            swmVersionFilter={swmVersionFilter}
            setSWMVersionFilter={setSWMVersionFilter}
            swmNameFilterSearch={swmNameFilterSearch}
            setSWMNameFilterSearch={setSWMNameFilterSearch}
            swmVersionFilterSearch={swmVersionFilterSearch}
            setSWMVersionFilterSearch={setSWMVersionFilterSearch}
            swmSortField={swmSortField}
            setSWMSortField={setSWMSortField}
            swmSortOrder={swmSortOrder}
            setSWMSortOrder={setSWMSortOrder}
            swmListToFilter={swmListToFilter}
            filteredSWMList={sortedSWMList}
            sortedSWMList={sortedSWMList}
            onRedirectedActionDialogClose={clearRedirectedSWMActionParams}
            clearAllCounter={clearAllCounter}
          />
        </Box>
      </Box>
      <CustomDialogBoxV2
        open={openEEComponentChangeLogsDialog}
        onClose={handleEELogsDialogClose}
        title={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              gap:2
            }}
          >
            <Typography sx={{ ...componentStyle.font.cardHeadder }}>
              EE Components
            </Typography>
            <IconButton
              size="small"
              onClick={handleEEComponentCopyOpen}
              disabled={
                !selectedEEItem && !eeLogsDialogData && !selectedEEComponentDetails
              }
              aria-label="Copy EE Component"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>
        }
      >
        <Box sx={{ width: "450px", height: "500px" }}>
          <DetailsLogTab
            logData={eeLogsDialogData}
            ChangeLogsList={eeChangeLogsList}
            getLogsData={handleGetEEComponentChangeLogs}
            selectedComponent={selectedEEItem}
            zone_name={selectedEEComponentDetails?.zone_details?.zone_name}
            title={"Update Draft EE Component"}
            supplierOptionsList={supplierOptionsList}
            variantOptionList={variantOptionList}
            adminSettingsDetails={adminSettingsDetails}
            openEditDetailsDialog={openEEComponentChangeLogsDialog}
            setOpenEditDetailsDialog={setOpenEEComponentChangeLogsDialog}
            handleEEComponentReplace={handleEEComponentReplace}
            selectedEEComponentReplaceDetails={selectedEEItem}
            setSelectedEEComponentReplaceDetails={
              setSelectedEEComponentReplaceDetails
            }
            totalLogsCount={eeChangeLogsTotalCount}
            setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
            selectedZone={selectedZone}
            selectedZoneDetails={selectedZoneDetails}
            vsOptionsList={vsOptionsList}
            selectedEEComponentDetails={selectedEEComponentDetails}
            getEEComponentsList={getEEComponentsList}
            getSWModulesList={getSWModulesList}
            selectedProjectDetails={selectedProjectDetails}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
            type={"EE"}
            visibleTabs={eeDetailsDialogTabs}
          />
        </Box>
      </CustomDialogBoxV2>

      <Box>


        <CustomDialogBoxV2
          open={openRequestsDialog}
          onClose={handleRequestsDialogClose}
          title="Edit Requests"
        >
          <Box sx={{ width: "450px", height: "300px" }}>
            <RequestsAndLogsTabs
              getRequestsData={requestsData}
              getStatusLabel={getStatusLabel}
              reason={reason}
              setReason={setReason}
              reasonError={reasonError}
              setReasonError={setReasonError}
              handleSubmit={handleSubmit}
              ChangeLogsList={eeChangeLogsList}
              totalLogsCount={eeChangeLogsTotalCount}
              onClose={handleRequestsDialogClose}
              selectedProjectDetails={selectedProjectDetails}
              data={eeDataSelected}
            />
          </Box>
        </CustomDialogBoxV2>
        <CustomDialogBoxV2
          open={actionType1DialogOpen}
          onClose={handleActionType1DialogClose}
          title={"Action Required"}
          onClickSuccess={() => {
            setIsLinkSoftwareModulesScreen(true);
            navigate(
              `/dashboard/v2/link?ee_id=${selectedEEComponentDetails?.ee_id}&selectedZone=${selectedZone?.zone_position_name}`,
            );
          }} // Trigger the update process
          onClickReject={handleActionType1DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography>EE Component dependency linking is pending!</Typography>
          </Stack>
        </CustomDialogBoxV2>

        <CustomDialogBoxV2
          open={actionType2DialogOpen}
          onClose={handleActionType2DialogClose}
          title={"Action Required"}
          onClickSuccess={handleRedirectionToTypeApproval} // Trigger the update process
          onClickReject={handleActionType2DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography>
              EE Component intersection linking is pending!!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>

        <VlcEEComponentsDecisionMatrixDialog
          showAttributeDecisionMatrix={showAttributeDecisionMatrix}
          attributeDecisionMatrixContext={attributeDecisionMatrixContext}
          handleAttributeDecisionMatrixClose={handleAttributeDecisionMatrixClose}
          handleAttributeExecuteWorkflow={handleAttributeExecuteWorkflow}
          handleAttributeViewExecution={handleAttributeViewExecution}
          handleAttributeDecisionMatrixAction={handleAttributeDecisionMatrixAction}
          handelGetAttributeSimilerTickets={handelGetAttributeSimilerTickets}
          attributeSimilarTickets={attributeSimilarTickets}
          setAttributeSimilarTickets={setAttributeSimilarTickets}
          attributeSelectedEntity={attributeSelectedEntity}
          handelGetAttributeAiSuggestion={handelGetAttributeAiSuggestion}
          attributeSelectedTicket={attributeSelectedTicket}
          setSelectedTicket={setAttributeSelectedTicket}
          attributeOpenAiPanel={attributeOpenAiPanel}
          setAttributeOpenAiPanel={setAttributeOpenAiPanel}
          setAttributeCreateTicketData={setAttributeCreateTicketData}
          attributeDisabledStandards={attributeDisabledStandards}
        />

        {confirmArchiveDialogOpen && (
          <CustomDialogBoxV2
            open={confirmArchiveDialogOpen}
            onClose={() => {
              setConfirmArchiveDialogOpen(false);
              setItemToArchive(null);
            }}
            title="Confirm Changes"
            // dialogRadius="0rem"
            successButtonName="Confirm"
            rejectButtonName="Cancel"
            actionButtonStyle="continuous"
            onClickSuccess={handleConfirmArchiveEE}
            onClickReject={() => {
              setConfirmArchiveDialogOpen(false);
              setItemToArchive(null);
            }}
          >
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", alignItems: "left", textAlign: "left", gap: 1 }}>
              <Typography>
                This change will affect the downstream components, modules, dependencies and intersections, if any are linked to the element.
              </Typography>
              <Typography>
                Are you sure you want to proceed?
              </Typography>
            </Box>
          </CustomDialogBoxV2>
        )}

        {confirmRetrievalDialogOpen && (
          <CustomDialogBoxV2
            open={confirmRetrievalDialogOpen}
            onClose={() => {
              setConfirmRetrievalDialogOpen(false);
              setItemToRetrieve(null);
            }}
            title="Confirm Changes"
            // dialogRadius="0rem"
            successButtonName="Confirm"
            rejectButtonName="Cancel"
            actionButtonStyle="continuous"
            onClickSuccess={handleConfirmRetrieveEE}
            onClickReject={() => {
              setConfirmRetrievalDialogOpen(false);
              setItemToRetrieve(null);
            }}
          >
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", alignItems: "left", textAlign: "left", gap: 1 }}>
              <Typography>
                This change will affect the downstream components, modules, dependencies and intersections, if any are linked to the element.
              </Typography>
              <Typography>
                Are you sure you want to proceed?
              </Typography>
            </Box>
          </CustomDialogBoxV2>
        )}
      </Box>
    </Box>
  );
}

export default React.memo(VlcEEComponents);

const styles = {
  closeButton: {
    color: "black",
    position: "absolute",
    top: { xs: "2px", sm: "4px" },
    right: { xs: "4px", sm: "8px" },
  },
  eeCompMainContainer: {
    flex: "0 0 auto",
    minWidth: 0,
    width: "100%",
    height: "450px",
    minHeight: "450px",
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    justifyContent: "stretch",
    boxSizing: "border-box",
    marginBottom: { xs: "8px", sm: "10px", md: "15px" },
  },
  eeCompCard: {
    flex: 1,
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    height: "100%",
    boxShadow: 4,
    borderRadius: "3%",
    display: "flex",
    flexDirection: "column",
  },
  eeCompCardHeader: {
    py: { xs: 0.3, sm: 0.5 },
    backgroundColor: "lightgrey",
    borderBottom: "1px solid lightgrey",
    borderRadius: "2%",
  },
  eeCompCardContent: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    scrollbarColor: "#888 #f1f1f1",
    scrollbarWidth: "thin",
    px: { xs: 0.5, sm: 1 },
    py: { xs: 0.5, sm: 1 },
  },
  eeCompCardActions: {
    display: "flex",
    justifyContent: "flex-end",
    borderTop: "1px solid lightgrey",
    px: { xs: 0.5, sm: 1 },
    py: { xs: 0.5, sm: 1 },
  },
  eeCompsList: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: { xs: 0.5, sm: 1 },
    pl: { xs: 0.5, sm: 1 },
    pr: { xs: 0.2, sm: 0.5 },
    backgroundColor: "background.paper",
    borderRadius: 2,
    boxShadow: 1,
    cursor: "pointer",
    minHeight: { xs: 44, sm: 54 },
    maxWidth: "100%",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  eeCompsListItemContainer: {
    textAlign: "start",
    my: { xs: 0.5, sm: 1 },
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    gap: 0.5,
  },
  noDataAvailable: {
    textAlign: "center",
    mt: { xs: 1, sm: 2 },
    fontSize: { xs: "0.9rem", sm: "1rem" },
  },
  txtStyle: {
    width: "270px",
  },
  txtStyleR: {
    width: "270px",
  },
  edRpIconsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: { xs: 0.5, sm: 1 },
  },
  customTextFieldN: {
    width: "270px",
  },
  selectTxtStyle: {
    width: "270px",
  },
  paginationPaper: {
    display: "flex",
    justifyContent: "flex-end",
    p: { xs: "2px", sm: "3px" },
  },
};



function VlcEEComponentUpdateDialog({
  variantOptionList,
  openEEComponentUpdateDialog,
  setOpenEEComponentUpdateDialog,
  handleEEComponentUpdate,
  selectedEEComponentUpdateDetails,
  setSelectedEEComponentUpdateDetails,
  setIsLinkSoftwareModulesScreen,
  selectedZone,
  selectedEEComponentDetails,
  getEEComponentsList,
  getSWModulesList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  isVersionImpactFlow,
  setIsVersionImpactFlow,
  resumeAttributeImpactFlowTrigger,
  resumeVersionImpactFlowTrigger,
  onRedirectedActionDialogClose,
}) {
  let navigate = useNavigate();
  const [version, setVersion] = useState("");
  const [versionError, setVersionError] = useState("");
  const [showAttributeConfirmation, setShowAttributeConfirmation] =
    useState(false); // State for attribute impact dialog
  const [openUpdateFlowAttributeDialog, setOpenUpdateFlowAttributeDialog] =
    useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false); // State to track confirmation dialog
  const [
    isEditAttributeVersionDependencyFlow,
    setIsEditAttributeVersionDependencyFlow,
  ] = useState(false);
  const [
    isAttributeImpactUpdatePersisted,
    setIsAttributeImpactUpdatePersisted,
  ] = useState(false);
  const [discp, setDiscp] = useState("");
  const [discpError, setDiscpError] = useState("");
  const [actionType1DialogOpen, setActionType1DialogOpen] = useState(false);

  const [selectedVariantsMany, setSelectedVariantsMany] = useState([]);
  const [rulePath, setRulePath] = useState([]);

  const [showIntersectionImpactPrompt, setShowIntersectionImpactPrompt] =
    useState(false);
  const [actionType2DialogOpen, setActionType2DialogOpen] = useState(false);

  const [showDecisionMatrix, setShowDecisionMatrix] = useState(false);
  const [decisionMatrixContext, setDecisionMatrixContext] = useState(null);
  const [executionId, setExecutionId] = useState(null);
  const [similarTickets, setSimilarTickets] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const [openAiPanel, setOpenAiPanel] = useState(false);
  const [createTicketData, setCreateTicketData] = useState({});
  const [disabledStandards, setDisabledStandards] = useState([]);
  const dispatch = useDispatch();
  const [disableActionButton, setDisableActionButton] = useState(false);
  const lastResumeAttributeImpactFlowTriggerRef = useRef(0);
  const lastResumeVersionImpactFlowTriggerRef = useRef(0);

  useEffect(() => {
    if (
      openEEComponentUpdateDialog &&
      selectedEEComponentUpdateDetails
    ) {

      setVersion(selectedEEComponentUpdateDetails?.version || "");
      setSelectedVariantsMany(
        variantOptionList
          ?.filter(
            (variant) =>
              variant?.value !== selectedSingleVariantDetails?.variant_id &&
              selectedEEComponentUpdateDetails?.variant_ids
                ?.map((variant) => variant)
                ?.includes(variant?.value),
          )
          ?.map((data) => data?.value) || [],
      );
      setDiscpError("");
    } else {
      setDiscpError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openEEComponentUpdateDialog, selectedEEComponentUpdateDetails]);

  useEffect(() => {
    if (
      resumeAttributeImpactFlowTrigger &&
      resumeAttributeImpactFlowTrigger !==
      lastResumeAttributeImpactFlowTriggerRef.current &&
      selectedEEComponentUpdateDetails
    ) {
      lastResumeAttributeImpactFlowTriggerRef.current =
        resumeAttributeImpactFlowTrigger;
      setShowAttributeConfirmation(false);
      setShowConfirmation(false);
      setShowIntersectionImpactPrompt(false);
      setIsVersionImpactFlow(false);
      setIsEditAttributeVersionDependencyFlow(false);
      setIsAttributeImpactUpdatePersisted(true);
      setOpenUpdateFlowAttributeDialog(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeAttributeImpactFlowTrigger, selectedEEComponentUpdateDetails]);

  useEffect(() => {
    if (
      resumeVersionImpactFlowTrigger &&
      resumeVersionImpactFlowTrigger !==
      lastResumeVersionImpactFlowTriggerRef.current &&
      selectedEEComponentUpdateDetails
    ) {
      lastResumeVersionImpactFlowTriggerRef.current =
        resumeVersionImpactFlowTrigger;
      setShowAttributeConfirmation(false);
      setOpenUpdateFlowAttributeDialog(false);
      setShowConfirmation(false);
      setShowIntersectionImpactPrompt(false);
      setIsAttributeImpactUpdatePersisted(false);
      setIsEditAttributeVersionDependencyFlow(false);
      setIsVersionImpactFlow(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeVersionImpactFlowTrigger, selectedEEComponentUpdateDetails]);

  useEffect(() => {
    if (openEEComponentUpdateDialog) {
      setDiscp("");
    }
  }, [openEEComponentUpdateDialog]);

  const isDecisionMatrixContextValid = isAttributeDecisionMatrixContextValid;

  const loadDecisionMatrixContext = async () => {
    const standardsRes = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...ruleEngineActions.decision_matrix_get_context,
          params: {
            component_type: "EE",
            component_id: selectedEEComponentDetails.ee_id,
            project_id: selectedProjectDetails.project_id,
            variant_id: selectedSingleVariantDetails.variant_id,
          },
          show_toast: false,
        }),
      ),
    );
    setOpenEEComponentUpdateDialog(false);
    setShowAttributeConfirmation(false); // Reset attribute confirmation on close
    setShowConfirmation(false); // Reset confirmation on close
    setShowIntersectionImpactPrompt(false);

    const contextData = standardsRes?.data;

    const isValid = isDecisionMatrixContextValid(contextData);

    if (!isValid) {
      console.warn(
        "Invalid decision matrix context, cannot proceed:",
        contextData,
      );
      setDecisionMatrixContext(null);
      setShowDecisionMatrix(false);
      return;
    }

    setDecisionMatrixContext(standardsRes?.data);

    setShowDecisionMatrix(true);
  };

  const handleExecuteWorkflow = async ({ mode, payload }) => {
    if (mode === "START") {
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.wfe_execution_start,
            params: {
              workflowId: payload.workflowId,
              version: payload.version,
            },
            show_toast: false,
          }),
        ),
      );

      setExecutionId(res.executionId);

      return res;
    }

    if (mode === "COMPLETE") {
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.wfe_execution_node_complete,
            params: {
              executionId: payload.executionId,
              nodeId: payload.nodeId,
              input: payload.input,
            },
            show_toast: false,
          }),
        ),
      );

      return res;
    }

    throw new Error(`Unsupported execution mode: ${mode}`);
  };

  const handleViewExecution = (executionId) => {
  };

  const clearUpdateEEComponentFields = () => {
    setSelectedVariantsMany([]);
    setVersion("");
    setVersionError("");
    setDiscpError("");
  };

  const isUpdateEEComponentValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;

    return (
      !versionError &&
      version?.trim() !== selectedEEComponentUpdateDetails?.version &&
      version?.trim()?.length >= 3 &&
      regexMin3?.test(version?.trim()) &&
      discp?.trim()?.length >= 3
    );
  };

  const handleCloseEEComponentUpdate = () => {
    setOpenEEComponentUpdateDialog(false);
    onRedirectedActionDialogClose?.();

    clearUpdateEEComponentFields();
    setSelectedEEComponentUpdateDetails(null);
    setShowAttributeConfirmation(false); // Reset attribute confirmation on close
    setOpenUpdateFlowAttributeDialog(false);
    setShowConfirmation(false); // Reset confirmation on close
    setShowIntersectionImpactPrompt(false);
    setIsVersionImpactFlow(false);
    setIsEditAttributeVersionDependencyFlow(false);
    setIsAttributeImpactUpdatePersisted(false);
    setDisableActionButton(false);
  };

  const handleUpdateClick = () => {
    if (isVersionImpactFlow) {
      void updateVersionImpactEEComponent();
      return;
    }

    setShowAttributeConfirmation(true);
  };

  const updateVersionImpactEEComponent = async () => {
    setDisableActionButton(true);

    try {
      const reqObj = {
        current_time: new Date().toISOString(),
        project_id: Number(selectedEEComponentUpdateDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedEEComponentUpdateDetails?.ee_id),
        variant_ids: selectedVariantsMany?.map(Number),
        version: version?.trim(),
        will_it_impact: true,
        reason: discp,
      };

      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.update_ee_component,
            params: reqObj,
            show_toast: true,
          }),
        ),
      );

      if (response?.error) {
        setDisableActionButton(false);
        return;
      }

      await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.update_action_type,
            params: {
              current_time: new Date().toISOString(),
              project_id: Number(selectedEEComponentUpdateDetails?.project_id),
              variant_id: Number(selectedSingleVariantDetails?.variant_id),
              ee_id: Number(selectedEEComponentUpdateDetails?.ee_id),
              is_dependency_linking: false,
              dependency: true,
            },
            show_toast: false,
          }),
        ),
      );

      setIsVersionImpactFlow(false);
      setIsEditAttributeVersionDependencyFlow(false);
      handleCloseEEComponentUpdate();
      setActionType1DialogOpen(true);
      getEEComponentsList();
      getSWModulesList();
    } catch (error) {
      console.error("Failed to update", error);
    }

    setDisableActionButton(false);
  };

  const handleEditAttributeVersionDependencyYes = async () => {
    setIsEditAttributeVersionDependencyFlow(false);
    await confirmUpdateAndRedirect();
  };

  const handleEditAttributeVersionIntersectionNo = async () => {
    setShowIntersectionImpactPrompt(false);
    setIsEditAttributeVersionDependencyFlow(false);
    await updateChangesWithoutStandardAndClose();
    handleCloseEEComponentUpdate();
  };

  const handleEditAttributeVersionIntersectionYes = async () => {
    setIsEditAttributeVersionDependencyFlow(false);
    await confirmUpdateAndRedirect({ willImptIntrOnly: true });
  };

  const handlePersistedAttributeDependencyYes = async () => {
    setDisableActionButton(true);
    setShowConfirmation(false);

    try {
      const currentDetails = selectedEEComponentUpdateDetails;
      await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.update_action_type,
            params: {
              current_time: new Date().toISOString(),
              project_id: Number(currentDetails?.project_id),
              variant_id: Number(selectedSingleVariantDetails?.variant_id),
              ee_id: Number(currentDetails?.ee_id),
              is_dependency_linking: false,
              dependency: true,
            },
            show_toast: false,
          }),
        ),
      );

      setIsAttributeImpactUpdatePersisted(false);
      handleCloseEEComponentUpdate();
      setActionType1DialogOpen(true);
      getEEComponentsList();
      getSWModulesList();
    } catch (error) {
      console.error("Failed to update action type", error);
      setDisableActionButton(false);
    }
  };

  const handlePersistedAttributeIntersectionNo = () => {
    setShowIntersectionImpactPrompt(false);
    setIsAttributeImpactUpdatePersisted(false);
    handleCloseEEComponentUpdate();
  };

  const handlePersistedAttributeIntersectionYes = async () => {
    setDisableActionButton(true);
    setShowIntersectionImpactPrompt(false);

    try {
      const currentDetails = selectedEEComponentUpdateDetails;
      await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.update_action_type,
            params: {
              current_time: new Date().toISOString(),
              project_id: Number(currentDetails?.project_id),
              variant_id: Number(selectedSingleVariantDetails?.variant_id),
              ee_id: Number(currentDetails?.ee_id),
              is_dependency_linking: false,
              intersection: true,
            },
            show_toast: false,
          }),
        ),
      );

      setIsAttributeImpactUpdatePersisted(false);
      handleCloseEEComponentUpdate();
      setActionType2DialogOpen(true);
      getEEComponentsList();
      getSWModulesList();
    } catch (error) {
      console.error("Failed to update action type", error);
      setDisableActionButton(false);
    }
  };

  const confirmUpdateAndRedirect = async ({
    willImptIntrOnly = false,
    showAttributeDialog = false,
  } = {}) => {
    setDisableActionButton(true);

    try {
      if (showAttributeDialog) {
        setShowAttributeConfirmation(false);

        const reqObj = {
          current_time: new Date().toISOString(),
          project_id: Number(selectedEEComponentUpdateDetails?.project_id),
          variant_id: Number(selectedSingleVariantDetails?.variant_id),
          ee_id: Number(selectedEEComponentUpdateDetails?.ee_id),
          variant_ids: selectedVariantsMany?.map(Number),
          version: version?.trim(),
          will_it_impact: true,
          reason: discp,
        };

        const response = await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...labCar.update_ee_component,
              params: reqObj,
              show_toast: true,
            }),
          ),
        );

        if (response?.error) {
          setDisableActionButton(false);
          return;
        }
        setOpenEEComponentUpdateDialog(false);
        setIsAttributeImpactUpdatePersisted(true);

        await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...labCar.update_action_type,
              params: {
                current_time: new Date().toISOString(),
                project_id: Number(selectedEEComponentUpdateDetails?.project_id),
                variant_id: Number(selectedSingleVariantDetails?.variant_id),
                ee_id: Number(selectedEEComponentUpdateDetails?.ee_id),
                is_dependency_linking: false,
                attribute: true,
              },
              show_toast: false,
            }),
          ),
        );

        setTimeout(() => {
          setOpenUpdateFlowAttributeDialog(true);
        }, 200);
        getEEComponentsList();
        getSWModulesList();
      } else {
        const reqObj = {
          current_time: new Date().toISOString(),
          project_id: Number(selectedEEComponentUpdateDetails?.project_id),
          variant_id: Number(selectedSingleVariantDetails?.variant_id),
          ee_id: Number(selectedEEComponentUpdateDetails?.ee_id),
          variant_ids: selectedVariantsMany?.map(Number),
          version: version?.trim(),
          will_it_impact: true,
          reason: discp,
        };

        const response = await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...labCar.update_ee_component,
              params: reqObj,
              show_toast: true,
            }),
          ),
        );

        if (!response.error) {
          const currentDetails = selectedEEComponentUpdateDetails;
          handleCloseEEComponentUpdate();
          if (willImptIntrOnly) {
            setActionType2DialogOpen(true);
            await Promise.resolve(
              dispatch(
                HandleApiActions({
                  ...labCar.update_action_type,
                  params: {
                    current_time: new Date().toISOString(),
                    project_id: Number(
                      currentDetails?.project_id,
                    ),
                    variant_id: Number(selectedSingleVariantDetails?.variant_id),
                    ee_id: Number(currentDetails?.ee_id),
                    is_dependency_linking: true,
                  },
                  show_toast: false,
                }),
              ),
            );
          } else {
            setActionType1DialogOpen(true);
          }
          getEEComponentsList();
          getSWModulesList();
        }
      }
    } catch (error) {
      console.error("Failed to update", error);
    }
    setDisableActionButton(false);
  };

  const updateChangesWithoutStandardAndClose = async () => {
    const reqObj = {
      current_time: new Date().toISOString(),
      variant_ids: selectedVariantsMany?.map(Number),
      project_id: Number(selectedEEComponentUpdateDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEComponentUpdateDetails?.ee_id),
      version: version?.trim(),
      will_it_impact: false,
      requires_homologation: false,
      reason: discp,
    };

    try {
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.update_ee_component,
            params: reqObj,
            show_toast: true,
          }),
        ),
      );
      getEEComponentsList();
      getSWModulesList();
      if (response?.data?.snow_resp) {
        // SNOW response handled
      }
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const handleActionType1DialogClose = () => {
    setActionType1DialogOpen(false);
  };

  const handleActionType2DialogClose = () => {
    setActionType2DialogOpen(false);
  };

  const handleRedirectionToTypeApproval = () => {
    // Redirect to type approval link screen for update component flow context
    const currentEEId = selectedEEComponentDetails?.ee_id;
    const currentZoneName = selectedZone?.zone_position_name;
    navigate(
      `/dashboard/v2/type-approval/link?ee_id=${currentEEId}&zone=${currentZoneName}&tab=2`,
    );
    setActionType2DialogOpen(false);
  };

  useEffect(() => {
    if (
      openEEComponentUpdateDialog &&
      selectedSingleVariantDetails?.variant_id != null
    ) {
      const id = selectedSingleVariantDetails.variant_id;
      const currentOptionValue =
        (variantOptionList || [])?.find((v) => String(v?.value) === String(id))
          ?.value ?? id;
      setSelectedVariantsMany((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const hasId = prevArr
          ?.map(String)
          ?.includes(String(currentOptionValue));
        if (hasId) return prevArr;
        return [currentOptionValue, ...prevArr];
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    openEEComponentUpdateDialog,
    selectedSingleVariantDetails,
    setSelectedVariantsMany,
  ]);

  const updateComponentAndCheckForDecisionMatrix = async ({
    standardCode,
  } = {}) => {
    try {

      const reCreateTicketParams = {
        details: createTicketData,
        reason: discp,
      };

      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.re_create_ticket_v1,
            params: reCreateTicketParams,
            show_toast: true,
          }),
        ),
      );

      if (!res?.error && standardCode) {
        handleTicketCreationSuccess(standardCode);
      }
      return res;
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const handleTicketCreationSuccess = (standardCode) => {
    setDisabledStandards((prev) => [...new Set([...prev, standardCode])]);
  };

  const handelGetSimilerTickets = async ({ standard_name } = {}) => {
    try {
      const reCreateTicketParams = {
        execution_id: executionId,
        entity_type: "ee",
        entity_id: Number(selectedEEComponentUpdateDetails?.ee_id),
        ...(standard_name == null ? {} : { standard_name }),
      };

      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.get_similar_tickets,
            params: reCreateTicketParams,
            show_toast: true,
          }),
        ),
      );

      setSimilarTickets(res?.data || []);
      setCreateTicketData(res?.data || []);
      return res;
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const handelGetAiSuggestion = (ticket) => {
    setSelectedEntity({
      type: "INTERSECTION",
      id: ticket?.intr_id,
      targetEntityTypes: ["ARTIFACT"],
      topK: 20,
      minConfidence: 0.6,
      description: "EE Component",
    });
    setOpenAiPanel(true);
    setSelectedTicket(ticket);
  };

  const handelCloseDecisionMatrix = () => {
    setShowDecisionMatrix(false);
    setDecisionMatrixContext(null);
    if (rulePath?.length > 0) setRulePath([]);
    setSimilarTickets([]);
    setSelectedEntity(null);
    setSelectedTicket(null);
    setCreateTicketData({});
    setDisabledStandards([]);
  };

  const getDependencyOnClickHandler = () => {
    if (isAttributeImpactUpdatePersisted) {
      return handlePersistedAttributeDependencyYes;
    }
    if (isEditAttributeVersionDependencyFlow) {
      return handleEditAttributeVersionDependencyYes;
    }
    return confirmUpdateAndRedirect;
  };

  return (
    <Box>
      <CustomDialogBoxV2
        open={openEEComponentUpdateDialog}
        onClose={handleCloseEEComponentUpdate}
        title={"Update EE Component"}
        onClickSuccess={handleUpdateClick} // Trigger the update process
        onDisabled={!isUpdateEEComponentValid()}
        onClickReject={handleCloseEEComponentUpdate}
        actionButtonStyle={"continuous"}
        successButtonName={"Update"}
        rejectButtonName={"Cancel"}
      >
        <Stack
          spacing={2}
          mb={2}
          justifyContent={"center"}
          sx={{ width: "450px" }}
        >
          <CustomFormTextFieldV2
            size={"small"}
            label={"Version"}
            required={true}
            type={"text"}
            fldType={"textR"}
            value={version}
            setValue={(value) => value?.length <= 40 && setVersion(value)}
            maxLength={40}
          />
          <MultiselectConnectorV2
            selectedValues={selectedVariantsMany}
            setSelectedValues={setSelectedVariantsMany}
            label={"Select Variants"}
            optionsList={(variantOptionList || [])?.filter((variants) => {
              const currentId = selectedSingleVariantDetails?.variant_id;
              const alreadyAdded = (
                selectedEEComponentUpdateDetails?.variant_ids || []
              )
                ?.map(String)
                ?.includes(String(variants?.value));
              return (
                alreadyAdded || String(variants?.value) === String(currentId)
              );
            })}
            immutableValues={(() => {
              const opt = (variantOptionList || [])?.find(
                (v) =>
                  String(v?.value) ===
                  String(selectedSingleVariantDetails?.variant_id),
              );
              return opt?.value == null ? [] : [opt.value];
            })()}
            placeholder={false}
          />
          <CustomFormTextFieldV2
            size={"small"}
            label={"Reason"}
            required={true}
            type={"text"}
            fldType={"textO"}
            value={discp}
            setValue={(value) => value?.length <= 100 && setDiscp(value)}
            valueError={discpError}
            setValueError={setDiscpError}
            maxLength={100}
          />

          <CustomDialogBoxV2
            open={showAttributeConfirmation}
            onClose={() => {
              setShowAttributeConfirmation(false);
            }}
            title="Action"
            dialogActions={
              <Box
                sx={{
                  display: "flex",
                  width: "350px",
                  justifyContent: "space-between",
                  height: "50px",
                }}
              >
                <CustomButtonV2
                  size="small"
                  type="cancel"
                  btnName="No"
                  handleOnClick={() => {
                    setShowAttributeConfirmation(false);
                    setTimeout(() => {
                      setShowConfirmation(true);
                    }, 200);
                  }}
                />
                <CustomButtonV2
                  size="small"
                  type="submit"
                  btnName="Yes"
                  handleOnClick={() => confirmUpdateAndRedirect({ showAttributeDialog: true })}
                  disabled={disableActionButton}
                />
              </Box>
            }
          >
            <Box
              sx={{
                width: "350px",
                height: "50px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "15px",
                }}
              >
                Will it impact the existing attributes?
              </Typography>
            </Box>
          </CustomDialogBoxV2>

        </Stack>
      </CustomDialogBoxV2>

      <AttributeUpdateDialog
        openAttributeUpdateDialog={openUpdateFlowAttributeDialog}
        setOpenAttributeUpdateDialog={setOpenUpdateFlowAttributeDialog}
        handleCloseAttributeUpdateDialog={() => {
          setOpenUpdateFlowAttributeDialog(false);
          onRedirectedActionDialogClose?.();
        }}
        onClickSuccess={() => {
          getEEComponentsList();
          getSWModulesList();
          setOpenUpdateFlowAttributeDialog(false);
        }}
        selectedComponent={selectedEEComponentUpdateDetails}
        componentType={"ee"}
        isAttributeImpactFlow
        variantId={selectedSingleVariantDetails?.variant_id}
        onIntersectionNo={loadDecisionMatrixContext}
        attributeImpactActionType={{ dependency: true }}
        onAttributeImpactComplete={() => {
          getEEComponentsList();
          getSWModulesList();
          setOpenUpdateFlowAttributeDialog(false);
          setActionType1DialogOpen(true);
        }}
      />

      <CustomDialogBoxV2
        open={showConfirmation}
        onClose={() => {
          setShowConfirmation(false);
        }}
        title="Action"
        dialogActions={
          <Box
            sx={{
              display: "flex",
              width: "350px",
              justifyContent: "space-between",
              height: "50px",
            }}
          >
            <CustomButtonV2
              size="small"
              type="cancel"
              btnName="No"
              handleOnClick={() => {
                setShowConfirmation(false);

                setTimeout(() => {
                  setShowIntersectionImpactPrompt(true);
                }, 200);
              }}
            />
            <CustomButtonV2
              size="small"
              type="submit"
              btnName="Yes"
              handleOnClick={getDependencyOnClickHandler()}
              disabled={disableActionButton}
            />
          </Box>
        }
      >
        <Box
          sx={{
            width: "350px",
            height: "50px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "15px",
            }}
          >
            Will it impact the existing dependencies?
          </Typography>
        </Box>
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        open={showIntersectionImpactPrompt}
        onClose={() => {
          setShowIntersectionImpactPrompt(false);
        }}
        title="Action"
        dialogActions={
          <Box
            sx={{
              display: "flex",
              width: "350px",
              justifyContent: "space-between",
              height: "50px",
            }}
          >
            <CustomButtonV2
              size="small"
              type="cancel"
              btnName="No"
              handleOnClick={() => {
                if (isAttributeImpactUpdatePersisted) {
                  handlePersistedAttributeIntersectionNo();
                  return;
                }

                if (isEditAttributeVersionDependencyFlow) {
                  void handleEditAttributeVersionIntersectionNo();
                  return;
                }

                void loadDecisionMatrixContext();
                void updateChangesWithoutStandardAndClose();
              }}
            />
            <CustomButtonV2
              size="small"
              type="submit"
              btnName="Yes"
              handleOnClick={() => {
                if (isAttributeImpactUpdatePersisted) {
                  void handlePersistedAttributeIntersectionYes();
                } else if (isEditAttributeVersionDependencyFlow) {
                  void handleEditAttributeVersionIntersectionYes();
                } else {
                  void confirmUpdateAndRedirect({ willImptIntrOnly: true });
                }
              }}
            />
          </Box>
        }
      >
        <Box
          sx={{
            width: "350px",
            height: "50px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "15px",
            }}
          >
            Will it impact the existing intersection?
          </Typography>
        </Box>
      </CustomDialogBoxV2>

      <Box>
        <CustomDialogBoxV2
          open={actionType1DialogOpen}
          onClose={handleActionType1DialogClose}
          title={"Action Required"}
          onClickSuccess={() => {
            setIsLinkSoftwareModulesScreen(true);
            navigate(
              `/dashboard/v2/link?ee_id=${selectedEEComponentDetails?.ee_id}&selectedZone=${selectedZone?.zone_position_name}`,
            );
          }} // Trigger the update process
          onClickReject={handleActionType1DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography sx={{ fontSize: "15px" }}>
              EE Component dependency linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>

        <CustomDialogBoxV2
          open={actionType2DialogOpen}
          onClose={handleActionType2DialogClose}
          title={"Action Required"}
          onClickSuccess={handleRedirectionToTypeApproval} // Trigger the update process
          onClickReject={handleActionType2DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography sx={{ fontSize: "15px" }}>
              EE Component intersection linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>



        <Dialog
          open={showDecisionMatrix}
          onClose={handelCloseDecisionMatrix}
          fullScreen
          PaperProps={{
            sx: {
              width: "100vw",
              height: "100vh",
              margin: 0,
              borderRadius: 0,
            },
          }}
        >
          <AppBar
            position="sticky"
            sx={{
              backgroundColor: "#26890D",
            }}
          >
            <Toolbar>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Decision Matrix
              </Typography>
              <IconButton color="inherit" onClick={handelCloseDecisionMatrix}>
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>

          <Box
            sx={{
              flex: 1,
              height: "calc(100vh - 64px)",
              overflow: "auto",
              p: 2,
            }}
          >
            {showDecisionMatrix && (
              <DecisionMatrixPanel
                context={decisionMatrixContext}
                key={showDecisionMatrix ? "open" : "closed"}
                onExecuteWorkflow={handleExecuteWorkflow}
                onViewExecution={handleViewExecution}
                onClose={handelCloseDecisionMatrix}
                onDecisionMatrixAction={
                  updateComponentAndCheckForDecisionMatrix
                }
                handelGetSimilerTickets={handelGetSimilerTickets}
                similarTickets={similarTickets}
                setSimilarTickets={setSimilarTickets}
                entityDetails={selectedEntity}
                handelGetAiSuggestion={handelGetAiSuggestion}
                selectedTicket={selectedTicket}
                setSelectedTicket={setSelectedTicket}
                openAiPanel={openAiPanel}
                setOpenAiPanel={setOpenAiPanel}
                setCreateTicketData={setCreateTicketData}
                disabledStandards={disabledStandards}
              />
            )}
          </Box>
        </Dialog>

      </Box>
    </Box>
  );
}

const normalizeArrayValue = (value) => {
  if (Array.isArray(value)) {
    return value.filter(
      (item) => item !== null && item !== undefined && item !== "",
    );
  }

  return value === null || value === undefined || value === "" ? [] : [value];
};

const normalizeKeywordIds = (keywords) =>
  (Array.isArray(keywords) ? keywords : [])
    .map((keyword) =>
      keyword && typeof keyword === "object"
        ? keyword?.id ?? keyword?.value
        : keyword,
    )
    .filter((id) => id !== null && id !== undefined && id !== "");

const haveArrayValuesChanged = (nextValues = [], existingValues = []) => {
  const next = normalizeArrayValue(nextValues).map(String).sort((a, b) => a.localeCompare(b));
  const existing = normalizeArrayValue(existingValues).map(String).sort((a, b) => a.localeCompare(b));

  return (
    next.length !== existing.length ||
    next.some((value, index) => value !== existing[index])
  );
};

const buildReplaceComponentPayload = (props) => {
  const {
    selectedEEComponentReplaceDetails,
    selectedSingleVariantDetails,
    selectedVariantsMany,
    assemblyNo,
    supplierName,
    version,
    summary,
    description,
    selectedKeywordIds,
    selectedVSIds,
    vcId,
    selectedASIL,
    discp,
    zoneDetails,
    willItImpact = true,
  } = props;

  return {
    current_time: new Date().toISOString(),
    variant_ids: selectedVariantsMany?.map(Number),
    project_id: Number(selectedEEComponentReplaceDetails?.project_id),
    variant_id: Number(selectedSingleVariantDetails?.variant_id),
    ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
    ...(zoneDetails && {
      zone_details: {
        zone_name: zoneDetails?.zone_name,
        position: zoneDetails?.position,
      },
    }),
    assembly_number: assemblyNo?.trim(),
    ...(supplierName?.length > 0 ? { supplier_name: supplierName } : null),
    version: version?.trim(),
    ...(summary && { summary }),
    description,
    keywords: selectedKeywordIds?.map(Number) || [],
    ...(selectedVSIds?.length > 0
      ? { vehicle_systems: selectedVSIds?.map(Number) || [] }
      : null),
    ...(vcId ? { vc_id: Number(vcId) } : null),
    other_fields: { asil_class: selectedASIL },
    will_it_impact: willItImpact,
    reason: discp,
  };
};

function VlcEEComponentReplaceDialog({
  title,
  getZoneEECountList,
  openEEComponentReplaceDialog,
  setOpenEEComponentReplaceDialog,
  handleEEComponentReplace,
  selectedEEComponentReplaceDetails,
  setSelectedEEComponentReplaceDetails,
  vsOptionsList,
  setIsLinkSoftwareModulesScreen,
  selectedZone,
  selectedZoneDetails,
  selectedEEComponentDetails,
  getEEComponentsList,
  getSWModulesList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  variantOptionList,
  supplierOptionsList,
}) {
  const navigate = useNavigate();
  const [selectedZoneName, setSelectedZoneName] = useState(
    selectedEEComponentReplaceDetails?.zone_details?.zone_name ||
      selectedZone?.zone_position_name ||
      "",
  );
  const [assemblyNo, setAssemblyNo] = useState("");

  const [assemblyNoError, setAssemblyNoError] = useState("");
  const [supplierName, setSupplierName] = useState([]);
  const [supplierNameError, setSupplierNameError] = useState("");
  const [version, setVersion] = useState("");
  const [versionError, setVersionError] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [summary, setSummary] = useState("");
  const [summaryError, setSummaryError] = useState("");
  const [selectedVSIds, setSelectedVSIds] = useState([]);
  const [keywordOptions, setKeywordOptions] = useState([]);
  const [selectedKeywordIds, setSelectedKeywordIds] = useState([]);
  const [vcId, setVcId] = useState("");
  const [vcIdError, setVcIdError] = useState(false);
  const [selectedVariantsMany, setSelectedVariantsMany] = useState([]);
  const [replaceTabValue, setReplaceTabValue] = useState(0);
  const [initialEEValues, setInitialEEValues] = useState(null);

  const [selectedASIL, setSelectedASIL] = useState(null);
  const [attributeFormData, setAttributeFormData] = useState({
    added: [],
    edited: [],
    deleted: [],
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [discp, setDiscp] = useState("");
  const [discpError, setDiscpError] = useState("");
  const [actionType1DialogOpen, setActionType1DialogOpen] = useState(false);
  const [ticketsDialogOpen, setTicketsDialogOpen] = useState(false);

  const [showIntersectionImpactPrompt, setShowIntersectionImpactPrompt] =
    useState(false);
  const [actionType2DialogOpen, setActionType2DialogOpen] = useState(false);

  const [rulePath, setRulePath] = useState([]);
  const [decisionMatrixContext, setDecisionMatrixContext] = useState(null);
  const [showDecisionMatrix, setShowDecisionMatrix] = useState(false);
  const [executionId, setExecutionId] = useState(null);
  const [similarTickets, setSimilarTickets] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openAiPanel, setOpenAiPanel] = useState(false);
  const [createTicketData, setCreateTicketData] = useState({});
  const [disabledStandards, setDisabledStandards] = useState([]);
  const dispatch = useDispatch();
  const [disableActionButton, setDisableActionButton] = useState(false);

  const handleEEAttributeEdit = useCallback((data = {}) => {
    const added = Array.isArray(data?.added) ? data.added : [];
    const edited = Array.isArray(data?.edited) ? data.edited : [];
    const deleted = Array.isArray(data?.deleted) ? data.deleted : [];

    setAttributeFormData((prev) => {
      const nextData = { added, edited, deleted };
      return JSON.stringify(prev) === JSON.stringify(nextData)
        ? prev
        : nextData;
    });
  }, []);

  const handleSelectedVehicleSystem = useCallback((vehicleSystemId) => {
    setSelectedVSIds(vehicleSystemId ? [Number(vehicleSystemId)] : []);
    setVcId("");
  }, []);

  const handleSelectedVehicleComponent = useCallback((vehicleComponentId) => {
    setVcId(vehicleComponentId || "");
  }, []);

  useEffect(() => {
    if (
      openEEComponentReplaceDialog &&
      selectedEEComponentReplaceDetails
    ) {
      const initAssembly = selectedEEComponentReplaceDetails?.assembly_number || "";
      const initSupplier = normalizeArrayValue(selectedEEComponentReplaceDetails?.supplier_name);
      const initVersion = selectedEEComponentReplaceDetails?.version || "";
      const initDescription = selectedEEComponentReplaceDetails?.description || "";
      const initSummary = selectedEEComponentReplaceDetails?.summary || "";
      const initKeywords = normalizeKeywordIds(selectedEEComponentReplaceDetails?.keywords);

      const rawVcDetails = selectedEEComponentReplaceDetails?.vehicle_component_details;
      const existingVcObj = Array.isArray(rawVcDetails) ? rawVcDetails[0] : rawVcDetails;
      const existingVsId =
        existingVcObj?.vs_id ||
        selectedEEComponentReplaceDetails?.vehicle_systems?.[0] ||
        selectedEEComponentReplaceDetails?.vehicle_system_details?.vs_id;
      const initVSIds = existingVsId ? [Number(existingVsId)] : [];
      const initVcId = existingVcObj?.vc_id ? String(existingVcObj.vcId || existingVcObj.vc_id) : "";

      const initASIL = selectedEEComponentReplaceDetails?.other_fields?.asil_class || null;

      const initVariants = (variantOptionList || [])
        ?.filter(
          (variant) =>
            variant?.value !== selectedSingleVariantDetails?.variant_id &&
            (selectedEEComponentReplaceDetails?.variant_ids || [])
              ?.map(String)
              ?.includes(String(variant?.value)),
        )
        ?.map((data) => data?.value) || [];

      const initialZone =
        selectedEEComponentReplaceDetails?.zone_details?.zone_name ||
        selectedProjectDetails?.project_zones?.find(
          (z) => z?.position === selectedEEComponentReplaceDetails?.zone_position,
        )?.zone_name ||
        selectedZone?.zone_position_name ||
        "";

      setAssemblyNo(initAssembly);
      setSupplierName(initSupplier);
      setVersion(initVersion);
      setDescription(initDescription);
      setSummary(initSummary);
      setSelectedKeywordIds(initKeywords);
      setSelectedVSIds(initVSIds);
      setVcId(initVcId);
      setSelectedASIL(initASIL);
      setSelectedVariantsMany(initVariants);
      setSelectedZoneName(initialZone);
      setAttributeFormData({ added: [], edited: [], deleted: [] });

      setInitialEEValues({
        assemblyNo: initAssembly,
        supplierName: initSupplier,
        version: initVersion,
        description: initDescription,
        summary: initSummary,
        keywords: initKeywords,
        vsIds: initVSIds,
        vcId: initVcId,
        asil: initASIL,
        variants: initVariants,
        zoneName: initialZone,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openEEComponentReplaceDialog, selectedEEComponentReplaceDetails]);

  useEffect(() => {
    if (!openEEComponentReplaceDialog) return;

    dispatch(
      HandleApiActions({
        api_name: "/SCM/settings/view/keywords",
        https_method: "GET",
        params: { type: 2 },
        ignore_app_loading_spinner: true,
      }),
    ).then((response) => {
      const keywords = Array.isArray(response?.data) ? response.data : [];
      setKeywordOptions(
        keywords.map((keyword) => ({
          value: keyword?.id,
          label: keyword?.name,
        })),
      );
    });
  }, [dispatch, openEEComponentReplaceDialog]);

  useEffect(() => {
    if (openEEComponentReplaceDialog) {
      setDiscp("");
      setDiscpError("");
      setReplaceTabValue(0);
    } else {
      setInitialEEValues(null);
    }
  }, [openEEComponentReplaceDialog]);

  const clearReplaceEEComponentFields = () => {
    setSelectedZoneName("");
    setAssemblyNo("");
    setAssemblyNoError("");
    setSupplierName([]);
    setSupplierNameError("");
    setVersion("");
    setVersionError("");
    setDescription("");
    setDescriptionError("");
    setSummary("");
    setSelectedVSIds([]);
    setSelectedKeywordIds([]);
    setVcId("");
    setSelectedASIL(null);
    setDiscp("");
    setDiscpError("");
    setSelectedVariantsMany([]);
    setAttributeFormData({ added: [], edited: [], deleted: [] });
    setInitialEEValues(null);
  };

  const fallbackZoneDetails = selectedZone?.zone_position_name
    ? selectedProjectDetails?.project_zones?.find(
        (z) => z?.zone_name === selectedZone?.zone_position_name,
      ) || null
    : null;

  const resolvedZoneDetails = selectedProjectDetails?.is_zonal
    ? selectedProjectDetails?.project_zones?.find(
        (z) => z?.zone_name === selectedZoneName || z?.position === selectedZoneName,
      ) || fallbackZoneDetails
    : null;

  const resolvedZonePosition = selectedProjectDetails?.is_zonal
    ? resolvedZoneDetails?.position ??
      selectedProjectDetails?.project_zones?.find(
        (z) => z?.zone_name === selectedZoneName || z?.position === selectedZoneName,
      )?.position ??
      selectedProjectDetails?.project_zones?.find(
        (z) => z?.zone_name === selectedZone?.zone_position_name,
      )?.position
    : undefined;

  const isReplaceEETab0Valid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;

    return (
      !assemblyNoError &&
      !supplierNameError &&
      !versionError &&
      !descriptionError &&
      !vcIdError &&
      supplierName?.length > 0 &&
      selectedASIL?.length > 0 &&
      (!selectedProjectDetails?.is_zonal || Boolean(selectedZoneName)) &&
      Boolean(regexMin3?.test(assemblyNo?.trim())) &&
      Boolean(regexMin3?.test(version?.trim())) &&
      Boolean(assemblyNo?.trim()?.length >= 3) &&
      Boolean(version?.trim()?.length >= 3) &&
      Boolean(description?.trim()?.length >= 3) &&
      (summary?.trim()?.length === 0 || !summaryError) &&
      (summary?.trim()?.length === 0 || summary?.trim()?.length >= 3)
    );
  };

  const haveDetailsFieldsChanged = () => {
    if (!initialEEValues) return false;

    const assemblyChanged =
      assemblyNo?.trim() !== (initialEEValues.assemblyNo || "")?.trim();

    const versionChanged =
      version?.trim() !== (initialEEValues.version || "")?.trim();

    const descriptionChanged =
      description?.trim() !== (initialEEValues.description || "")?.trim();

    const summaryChanged =
      (summary || "")?.trim() !== (initialEEValues.summary || "")?.trim();

    const zoneChanged =
      Boolean(selectedProjectDetails?.is_zonal) &&
      title !== "Replace EE Component" &&
      title !== "Add Draft EE Component" &&
      Boolean(selectedZoneName) &&
      selectedZoneName !== (initialEEValues.zoneName || "");

    const supplierChanged = haveArrayValuesChanged(
      supplierName,
      initialEEValues.supplierName,
    );

    const keywordsChanged = haveArrayValuesChanged(
      selectedKeywordIds,
      initialEEValues.keywords,
    );

    const vehicleSystemsChanged = haveArrayValuesChanged(
      selectedVSIds,
      initialEEValues.vsIds,
    );

    const vehicleComponentChanged =
      String(vcId || "") !== String(initialEEValues.vcId || "");

    const asilChanged =
      (selectedASIL || null) !== (initialEEValues.asil || null);

    const variantsChanged = haveArrayValuesChanged(
      selectedVariantsMany,
      initialEEValues.variants,
    );

    return (
      assemblyChanged ||
      versionChanged ||
      descriptionChanged ||
      summaryChanged ||
      zoneChanged ||
      supplierChanged ||
      keywordsChanged ||
      vehicleSystemsChanged ||
      vehicleComponentChanged ||
      asilChanged ||
      variantsChanged
    );
  };

  const haveAttributesChanged = () =>
    Boolean(
      attributeFormData?.added?.length > 0 ||
      attributeFormData?.edited?.length > 0 ||
      attributeFormData?.deleted?.length > 0,
    );

  const haveAnyEEFieldsChanged = () =>
    haveDetailsFieldsChanged() || haveAttributesChanged();

  const isReplaceEEComponentDetailsValid = () =>
    isReplaceEETab0Valid() && haveAnyEEFieldsChanged();

  const isReasonValid =
    Boolean(discp?.trim() && discp?.trim()?.length >= 3) && !discpError;

  const isReplaceEEComponentValid = () =>
    isReplaceEEComponentDetailsValid() && isReasonValid;

  const isReplaceEENextEnabled = () => isReplaceEETab0Valid();

  const isUpdateDraftEEComponentValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;
    return (
      (!selectedProjectDetails?.is_zonal || Boolean(selectedZoneName)) &&
      assemblyNo?.trim() !== "" &&
      assemblyNo?.trim()?.length >= 3 &&
      !assemblyNoError &&
      regexMin3?.test(assemblyNo?.trim()) &&
      supplierName?.length > 0 &&
      version?.trim() !== "" &&
      version?.trim()?.length >= 3 &&
      !versionError &&
      regexMin3?.test(version?.trim()) &&
      description?.trim() !== "" &&
      description?.trim()?.length >= 3 &&
      !descriptionError &&
      selectedASIL?.length > 0 &&
      (summary?.trim()?.length === 0 || !summaryError) &&
      (summary?.trim()?.length === 0 || summary?.trim()?.length >= 3)
    );
  };
  const isUpdateAgainDraftEEComponentValid = () => {
    return (
      Boolean(selectedZoneName) ||
      assemblyNo?.trim() !== "" ||
      supplierName?.length > 0 ||
      version?.trim() !== "" ||
      description?.trim() !== "" ||
      summary?.trim() !== "" ||
      selectedVSIds?.length > 0 ||
      Boolean(vcId) ||
      selectedKeywordIds?.length > 0 ||
      selectedASIL?.length > 0
    );

  };

  const handleCloseEEComponentReplace = () => {
    setOpenEEComponentReplaceDialog(false);

    clearReplaceEEComponentFields();

    setShowConfirmation(false); // Reset confirmation on close
    setShowIntersectionImpactPrompt(false);
  };

  const handleUpdateClick = () => {
    setShowConfirmation(true); // Show confirmation dialog when update is clicked
  };

  const handleReplaceDialogSuccess = () => {
    if (
      (title === "Replace EE Component" ||
        title === "Add Draft EE Component" || title === "Draft EE Component") &&
      replaceTabValue === 0
    ) {
      setReplaceTabValue(1);
      return;
    }

    if (title === "Draft EE Component") {
      void confirmUpdateAndRedirect({ willImptIntrOnly: false });
    } else if (title === "Replace EE Component") {
      handleUpdateClick();
    } else {
      handleUpdateDraftClickEE();
    }
  };

  const handleUpdateDraftClickEE = () => {
    const reqObj = {
      project_id: selectedProjectDetails?.project_id,
      variant_id: selectedSingleVariantDetails?.variant_id,
      ee_id: selectedEEComponentDetails?.ee_id,
      ...(resolvedZoneDetails && {
        zone_details: {
          zone_name: resolvedZoneDetails?.zone_name,
          position: resolvedZoneDetails?.position,
        },
      }),
      ...(selectedProjectDetails?.is_zonal &&
        resolvedZonePosition !== undefined &&
        resolvedZonePosition !== null && {
          zone_position: resolvedZonePosition,
        }),
      ...(selectedVariantsMany?.length > 0
        ? { variant_ids: selectedVariantsMany }
        : []),
      save_as_draft: false,
      assembly_number: assemblyNo,
      supplier_name: supplierName,

      version: version?.trim(),
      ...(summary ? { summary: summary } : null),
      description: description,
      keywords: selectedKeywordIds?.map(Number) || [],
      ...(selectedVSIds?.length > 0
        ? { vehicle_systems: selectedVSIds?.map(Number) || [] }
        : null),
      ...(vcId ? { vc_id: Number(vcId) } : null),

      other_fields: {
        asil_class: selectedASIL,
      },

      current_time: moment().format(),
    };
    dispatch(
      HandleApiActions({
        api_name: "/VLC/ee_components/update/draft",
        https_method: "POST",
        action_type: actionType.ADD_EE_COMPONENT_DRAFT,
        description: "Add in Save As Draft EE Component",
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      getEEComponentsList();
      handleCloseEEComponentReplace();
    });
  };

  const handleUpdateAgainDraftClickEE = () => {
    const reqObj = {
      project_id: selectedProjectDetails?.project_id,
      variant_id: selectedSingleVariantDetails?.variant_id,
      ee_id: selectedEEComponentDetails?.ee_id,
      ...(resolvedZoneDetails && {
        zone_details: {
          zone_name: resolvedZoneDetails?.zone_name,
          position: resolvedZoneDetails?.position,
        },
      }),
      ...(selectedProjectDetails?.is_zonal &&
        resolvedZonePosition !== undefined &&
        resolvedZonePosition !== null && {
          zone_position: resolvedZonePosition,
        }),
      variant_ids: selectedVariantsMany,
      save_as_draft: true,
      assembly_number: assemblyNo,
      ...(supplierName?.length > 0 ? { supplier_name: supplierName } : null),

      ...(version ? { version: version?.trim() } : null),
      ...(summary ? { summary: summary } : null),
      ...(description ? { description: description } : null),
      keywords: selectedKeywordIds?.map(Number) || [],
      ...(selectedVSIds?.length > 0
        ? { vehicle_systems: selectedVSIds?.map(Number) || [] }
        : null),
      ...(vcId ? { vc_id: Number(vcId) } : null),
      ...(selectedASIL?.length > 0
        ? {
          other_fields: {
            asil_class: selectedASIL,
          },
        }
        : null),
      current_time: moment().format(),
    };

    dispatch(
      HandleApiActions({
        api_name: "/VLC/ee_components/update/draft",
        https_method: "POST",
        action_type: actionType.ADD_EE_COMPONENT_DRAFT,
        description: "Add in Save As Draft EE Component",
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      getEEComponentsList();
      handleCloseEEComponentReplace();
    });
  };

  const updateAttribute = async () => {
    const addedAttributes = Array.isArray(attributeFormData?.added)
      ? attributeFormData.added
      : [];
    const updatedAttributes = Array.isArray(attributeFormData?.edited)
      ? attributeFormData.edited
      : [];
    const deletedAttributes = Array.isArray(attributeFormData?.deleted)
      ? attributeFormData.deleted
      : [];

    if (
      addedAttributes.length > 0 ||
      updatedAttributes.length > 0 ||
      deletedAttributes.length > 0
    ) {
      const attributeResp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.updateEEAttribute,
            params: {
              entity_id: Number(selectedEEComponentReplaceDetails?.ee_id),
              new_attributes: addedAttributes,
              updated_attributes: updatedAttributes,
              deleted_attributes: deletedAttributes,
            },
            show_toast: false,
          }),
        ),
      );

      if (attributeResp?.error) {
        setDisableActionButton(false);
      }
    }
  };

  const confirmUpdateAndRedirect = async ({ willImptIntrOnly = false }) => {
    setDisableActionButton(true);
    if (selectedEEComponentReplaceDetails?.ee_id) {
      await updateAttribute();

      const reqObj = buildReplaceComponentPayload({
        selectedEEComponentReplaceDetails,
        selectedSingleVariantDetails,
        selectedVariantsMany,
        assemblyNo,
        supplierName,
        version,
        summary,
        description,
        selectedKeywordIds,
        selectedVSIds,
        vcId,
        selectedASIL,
        discp,
        zoneDetails: resolvedZoneDetails,
        willItImpact: title !== "Draft EE Component",
      });

      try {
        const response = await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...labCar.replace_ee_component,
              params: reqObj,
              show_toast: true,
            }),
          ),
        );
        if (!response.error) {
          handleCloseEEComponentReplace();
          if (title !== "Draft EE Component") {
            if (willImptIntrOnly) {
              setActionType2DialogOpen(true);
              await Promise.resolve(
                dispatch(
                  HandleApiActions({
                    ...labCar.update_action_type,
                    params: {
                      current_time: new Date().toISOString(),
                      project_id: Number(
                        selectedEEComponentReplaceDetails?.project_id,
                      ),
                      variant_id: Number(selectedSingleVariantDetails?.variant_id),
                      ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
                      is_dependency_linking: true,
                    },
                    show_toast: false,
                  }),
                ),
              );
            } else {
              setActionType1DialogOpen(true);
            }
          }
          getEEComponentsList();
          getSWModulesList();
        }
      } catch (error) {
        console.error("Failed to replace", error);
      }
    }
    setDisableActionButton(false);
  };

  const replaceComponentAndCheckForDecisionMatrix = async ({
    standardCode,
  } = {}) => {
    // Check decision matrix for component replacement flow
    try {
      const reCreateTicketParams = {
        details: createTicketData,
        reason: discp,
        flow: "REPLACE_MATRIX",
      };

      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.re_create_ticket_v1,
            params: reCreateTicketParams,
            show_toast: true,
          }),
        ),
      );

      if (!res?.error && standardCode) {
        handleTicketCreationSuccess(standardCode);
      }
      return res;


    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const handleTicketCreationSuccess = (standardCode) => {
    setDisabledStandards((prev) => [...new Set([...prev, standardCode])]);
  };

  const isDecisionMatrixContextValid = isAttributeDecisionMatrixContextValid;

  const loadDecisionMatrixContext = async () => {
    const standardsRes = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...ruleEngineActions.decision_matrix_get_context,
          params: {
            component_type: "EE",
            component_id: selectedEEComponentDetails.ee_id,
            project_id: selectedProjectDetails.project_id,
            variant_id: selectedSingleVariantDetails.variant_id,
          },
          show_toast: false,
        }),
      ),
    );
    setOpenEEComponentReplaceDialog(false);
    setShowConfirmation(false); // Reset confirmation on close
    setShowIntersectionImpactPrompt(false);

    const contextData = standardsRes?.data;

    const isValid = isDecisionMatrixContextValid(contextData);

    if (!isValid) {
      console.warn(
        "Invalid decision matrix context, cannot proceed:",
        contextData,
      );
      setDecisionMatrixContext(null);
      setShowDecisionMatrix(false);
      return;
    }

    setDecisionMatrixContext(standardsRes?.data);

    setShowDecisionMatrix(true);
  };

  const replaceChangesWithoutStandardAndClose = async () => {
    await updateAttribute();
    const reqObj = {
      current_time: new Date().toISOString(),
      variant_ids: selectedVariantsMany,
      project_id: Number(selectedEEComponentReplaceDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
      ...(resolvedZoneDetails && {
        zone_details: {
          zone_name: resolvedZoneDetails?.zone_name,
          position: resolvedZoneDetails?.position,
        },
      }),
      assembly_number: assemblyNo?.trim(),
      ...(supplierName?.length > 0 ? { supplier_name: supplierName } : null),
      version: version?.trim(),
      ...(summary && { summary }),
      description: description,
      keywords: selectedKeywordIds?.map(Number) || [],
      ...(selectedVSIds?.length > 0
        ? { vehicle_systems: selectedVSIds?.map(Number) || [] }
        : null),
      ...(vcId ? { vc_id: Number(vcId) } : null),
      other_fields: {
        asil_class: selectedASIL,
      },
      will_it_impact: false,
      reason: discp,
      requires_homologation: false,
    };

    try {
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.replace_ee_component,
            params: reqObj,
            show_toast: true,
          }),
        ),
      );
      getEEComponentsList();
      getSWModulesList();

      if (response?.data?.snow_resp) {
        // SNOW response handled
      }
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const handleActionType1DialogClose = () => {
    setActionType1DialogOpen(false);
  };

  const handleActionType2DialogClose = () => {
    setActionType2DialogOpen(false);
  };

  const handleRedirectionToTypeApproval = () => {
    // Redirect to type approval link screen for replacement dialog context
    const targetEEId = selectedEEComponentDetails?.ee_id;
    const targetZoneName = selectedZone?.zone_position_name;
    navigate(
      `/dashboard/v2/type-approval/link?ee_id=${targetEEId}&zone=${targetZoneName}&tab=2`,
    );
    setActionType2DialogOpen(false);
  };

  const handleTicketsDialogClose = () => {
    setTicketsDialogOpen(false);
  };

  const handleExecuteWorkflow = async ({ mode, payload }) => {
    // Execute rule engine workflow for replacement component dialog
    if (mode === "START") {
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.wfe_execution_start,
            params: {
              workflowId: payload?.workflowId,
              version: payload?.version,
            },
            show_toast: false,
          }),
        ),
      );

      setExecutionId(res.executionId);

      return res;
    }

    if (mode === "COMPLETE") {
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.wfe_execution_node_complete,
            params: {
              executionId: payload.executionId,
              nodeId: payload.nodeId,
              input: payload.input,
            },
            show_toast: false,
          }),
        ),
      );

      return res;
    }

    throw new Error(`Unsupported execution mode: ${mode}`);
  };

  const handleViewExecution = (executionId) => {
  };

  useEffect(() => {
    if (
      openEEComponentReplaceDialog &&
      selectedSingleVariantDetails?.variant_id != null
    ) {
      const id = selectedSingleVariantDetails.variant_id;
      const currentOptionValue =
        (variantOptionList || [])?.find((v) => String(v?.value) === String(id))
          ?.value ?? id;
      setSelectedVariantsMany((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const hasId = prevArr?.map(String).includes(String(currentOptionValue));
        if (hasId) return prevArr;
        return [currentOptionValue, ...prevArr];
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    openEEComponentReplaceDialog,
    selectedSingleVariantDetails,
    setSelectedVariantsMany,
  ]);

  const handelGetSimilerTickets = async ({ standard_name } = {}) => {
    try {
      const reCreateTicketParams = {
        execution_id: executionId,
        entity_type: "ee",
        entity_id: Number(selectedEEComponentDetails?.ee_id),
        ...(standard_name == null ? {} : { standard_name }),
      };

      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.get_similar_tickets,
            params: reCreateTicketParams,
            show_toast: true,
          }),
        ),
      );

      setSimilarTickets(res?.data || []);
      setCreateTicketData(res?.data || []);
      return res;
    } catch (error) {
      console.error("Failed to update", error);
    }
  };
  const handelGetAiSuggestion = (ticket) => {
    // Get AI suggestion for selected ticket in replacement dialog context
    if (!ticket) return;
    setSelectedEntity({
      type: "INTERSECTION",
      id: ticket?.intr_id,
      targetEntityTypes: ["ARTIFACT"],
      topK: 20,
      minConfidence: 0.6,
      description: "EE Component",
    });
    setOpenAiPanel(true);
    setSelectedTicket(ticket);
  };

  const handelCloseDecisionMatrix = () => {
    // Close decision matrix dialog in replacement dialog context
    setShowDecisionMatrix(false);
    setDecisionMatrixContext(null);
    if (rulePath?.length > 0) setRulePath([]);
    if (vcIdError) setVcIdError(false);
    setSimilarTickets([]);
    setSelectedEntity(null);
    setSelectedTicket(null);
    setCreateTicketData({});
    setDisabledStandards([]);
    return true;
  };
  const getSuccessButtonName = (dialogTitle, tabVal) => {
    if (dialogTitle === "Replace EE Component" || dialogTitle === "Add Draft EE Component" || dialogTitle === "Draft EE Component") {
      if (tabVal === 0) return "Next";
      return (dialogTitle === "Replace EE Component") ? "Replace" : "Add";
    }
    return "Add";
  };

  const getReplaceDialogDisabled = (dialogTitle, tabVal) => {
    if (dialogTitle === "Replace EE Component" || dialogTitle === "Draft EE Component") {
      return tabVal === 0 ? !isReplaceEENextEnabled() : !isReplaceEEComponentValid();
    }
    return tabVal === 0 ? !isUpdateAgainDraftEEComponentValid() : !isUpdateDraftEEComponentValid();
  };

  const getReplaceRejectButtonName = (dialogTitle, tabVal) => {
    if (dialogTitle === "Replace EE Component" || dialogTitle === "Draft EE Component") {
      return "Cancel";
    }
    return tabVal === 1 ? "Save Draft" : null;
  };

  return (
    <Box>

      <CustomDialogBoxV2
        open={openEEComponentReplaceDialog}
        onClose={handleCloseEEComponentReplace}
        title={title}
        successButtonName={getSuccessButtonName(title, replaceTabValue)}
        rejectButtonName={getReplaceRejectButtonName(title, replaceTabValue)}
        onDisabled={getReplaceDialogDisabled(title, replaceTabValue)}
        rejectDisabled={
          title === "Replace EE Component" || title === "Draft EE Component"
            ? false
            : !isUpdateAgainDraftEEComponentValid()
        }
        rejectButtonType={title === "Replace EE Component" || title === "Draft EE Component" ? "cancel" : "other"}
        onClickSuccess={handleReplaceDialogSuccess}
        onClickReject={
          title === "Replace EE Component" || title === "Draft EE Component"
            ? handleCloseEEComponentReplace
            : handleUpdateAgainDraftClickEE
        }
        actionButtonStyle={"three"}
        otherButtonName={
          (title === "Replace EE Component" ||
            title === "Add Draft EE Component" || title === "Draft EE Component") &&
            replaceTabValue === 1
            ? "Back"
            : null
        }
        onOtherButtonClick={() => setReplaceTabValue(0)}
        disabled={false}
        typeForThree={"cancel"}
        otherButtonPlacement={"left"}
      >
        <CustomTabsV2
          defaultTab={replaceTabValue}
          value={replaceTabValue}
          onTabChange={(newValue) => setReplaceTabValue(newValue)}
          tabs={[
            {
              name: "Details",
              content: (
                <Stack
                  spacing={2}
                  mb={2}
                  justifyContent={"center"}
                  sx={{ width: "450px" }}
                >
                  {selectedProjectDetails?.is_zonal &&
                    title !== "Replace EE Component" &&
                    title !== "Add Draft EE Component" && (
                      <CustomSelectV2
                        size={"small"}
                        label={"Select Zone"}
                        required={true}
                        optionsList={
                          selectedProjectDetails?.project_zones?.map((zone) => ({
                            value: zone?.zone_name,
                            label: zone?.zone_name,
                          })) || []
                        }
                        selectedValue={selectedZoneName || " "}
                        handleOnChange={(e) => setSelectedZoneName(e.target.value)}
                      />
                    )}
                  <CustomFormTextFieldV2
                    size={"small"}
                    label={"Assembly No"}
                    required={true}
                    type={"text"}
                    fldType={"textR"}
                    value={assemblyNo || " "}
                    setValue={(value) =>
                      value?.length <= 40 && setAssemblyNo(value)
                    }
                    maxLength={40}
                  />
                  <CustomFormTextFieldV2
                    size={"small"}
                    label={"Version"}
                    required={true}
                    type={"text"}
                    fldType={"textR"}
                    value={version}
                    setValue={(value) =>
                      value?.length <= 40 && setVersion(value)
                    }
                    maxLength={40}
                  />
                  <CustomFormTextFieldV2
                    size={"small"}
                    label={"Description"}
                    required={true}
                    type={"text"}
                    fldType={"text"}
                    value={description}
                    setValue={(value) =>
                      value?.length <= 500 && setDescription(value)
                    }
                    valueError={descriptionError}
                    setValueError={setDescriptionError}
                    maxLength={500}
                  />
                  <CustomFormTextFieldV2
                    size={"small"}
                    label={"Summary"}
                    type={"text"}
                    fldType={"text"}
                    value={summary}
                    setValue={(value) =>
                      value?.length <= 250 && setSummary(value)
                    }
                    valueError={summaryError}
                    setValueError={setSummaryError}
                    maxLength={250}
                  />
                  <MultiselectConnectorV2
                    multiple
                    selectedValues={supplierName || []}
                    setSelectedValues={setSupplierName}
                    label={"Select Supplier Name"}
                    optionsList={supplierOptionsList}
                    placeholder={false}
                  />

                  <SelectVehicleComponentForVehicleSystem
                    open={openEEComponentReplaceDialog}
                    projectId={selectedProjectDetails?.project_id}
                    variantId={selectedSingleVariantDetails?.variant_id}
                    isZonal={selectedProjectDetails?.is_zonal}
                    zonePosition={selectedZoneDetails?.position}
                    getSelectedVehicleSystem={handleSelectedVehicleSystem}
                    getSelectedVehicleComponent={handleSelectedVehicleComponent}
                    setVcIdError={setVcIdError}
                    vcId={
                      selectedEEComponentReplaceDetails
                        ?.vehicle_component_details[0]?.vc_id
                    }
                    vsId={
                      selectedEEComponentReplaceDetails
                        ?.vehicle_component_details[0]?.vs_id
                    }
                  />
                  <CustomSelectV2
                    size={"small"}
                    label={"Select ASIL Class"}
                    required={true}
                    optionsList={["A", "B", "C", "D"]?.map((el) => ({
                      value: el,
                      label: el,
                    }))}
                    selectedValue={selectedASIL || " "}
                    handleOnChange={(e) => setSelectedASIL(e.target.value)}
                  />
                  <MultiselectConnectorV2
                    multiple
                    selectedValues={selectedKeywordIds || []}
                    setSelectedValues={setSelectedKeywordIds}
                    label={"Keywords"}
                    optionsList={keywordOptions}
                    placeholder={false}
                  />
                  <MultiselectConnectorV2
                    multiple
                    selectedValues={selectedVariantsMany}
                    setSelectedValues={setSelectedVariantsMany}
                    label={"Select Variants"}
                    optionsList={(variantOptionList || [])?.filter(
                      (variants) => {
                        const currentId =
                          selectedSingleVariantDetails?.variant_id;
                        const alreadyAdded = (
                          selectedEEComponentReplaceDetails?.variant_ids || []
                        )
                          ?.map(String)
                          ?.includes(String(variants?.value));
                        return (
                          alreadyAdded ||
                          String(variants?.value) === String(currentId)
                        );
                      },
                    )}
                    immutableValues={(() => {
                      const opt = (variantOptionList || [])?.find(
                        (v) =>
                          String(v?.value) ===
                          String(selectedSingleVariantDetails?.variant_id),
                      );
                      return opt?.value == null ? [] : [opt.value];
                    })()}
                    placeholder={false}
                  />
                </Stack>
              ),
            },
            {
              name: "Attribute",
              content: (
                <Stack
                  spacing={2}
                  mb={2}
                  justifyContent={"center"}
                  sx={{ width: "450px" }}
                >
                  <AttributesForm
                    type={"EDIT"}
                    attributeFromApi={
                      selectedEEComponentReplaceDetails?.attributes || []
                    }
                    getFormData={handleEEAttributeEdit}
                    reason={discp}
                    setReason={setDiscp}
                    reasonError={discpError}
                    setReasonError={setDiscpError}
                    showReason={title === "Replace EE Component" || title === "Draft EE Component"}
                  />
                </Stack>
              ),
            },
          ]}
        />

        <CustomDialogBoxV2
          open={showConfirmation}
          onClose={() => {
            setShowConfirmation(false);
          }}
          title="Action"
          dialogActions={
            <Box
              sx={{
                display: "flex",
                width: "350px",
                justifyContent: "space-between",
                height: "50px",
              }}
            >
              <CustomButtonV2
                size="small"
                type="cancel"
                btnName="No"
                handleOnClick={() => {
                  setShowConfirmation(false);

                  setTimeout(() => {
                    setShowIntersectionImpactPrompt(true);
                  }, 200);
                }}
              />
              <CustomButtonV2
                size="small"
                type="submit"
                btnName="Yes"
                handleOnClick={confirmUpdateAndRedirect}
                disabled={disableActionButton}
              />
            </Box>
          }
        >
          <Box
            sx={{
              width: "350px",
              height: "50px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "15px",
              }}
            >
              Will it impact the existing dependencies?
            </Typography>
          </Box>
        </CustomDialogBoxV2>
        <CustomDialogBoxV2
          open={showIntersectionImpactPrompt}
          onClose={() => {
            setShowIntersectionImpactPrompt(false);
          }}
          title="Action"
          dialogActions={
            <Box
              sx={{
                display: "flex",
                width: "350px",
                justifyContent: "space-between",
                height: "50px",
              }}
            >
              <CustomButtonV2
                size="small"
                type="cancel"
                btnName="No"
                handleOnClick={() => {
                  void loadDecisionMatrixContext();
                  void replaceChangesWithoutStandardAndClose();
                }}
              />
              <CustomButtonV2
                size="small"
                type="submit"
                btnName="Yes"
                handleOnClick={() =>
                  void confirmUpdateAndRedirect({ willImptIntrOnly: true })
                }
              />
            </Box>
          }
        >
          <Box
            sx={{
              width: "350px",
              height: "50px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "15px",
              }}
            >
              Will it impact the existing intersection?
            </Typography>
          </Box>
        </CustomDialogBoxV2>
      </CustomDialogBoxV2>
      <Box>
        <CustomDialogBoxV2
          open={actionType1DialogOpen}
          onClose={handleActionType1DialogClose}
          title={"Action Required"}
          onClickSuccess={() => {
            setIsLinkSoftwareModulesScreen(true);
            navigate(
              `/dashboard/v2/link?ee_id=${selectedEEComponentDetails?.ee_id}&selectedZone=${selectedZone?.zone_position_name}`,
            );
          }} // Trigger the update process
          onClickReject={handleActionType1DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography>EE Component dependency linking is pending!</Typography>
          </Stack>
        </CustomDialogBoxV2>

        <CustomDialogBoxV2
          open={actionType2DialogOpen}
          onClose={handleActionType2DialogClose}
          title={"Action Required"}
          onClickSuccess={handleRedirectionToTypeApproval} // Trigger the update process
          onClickReject={handleActionType2DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography>
              EE Component intersection linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>

        <CustomDialogBoxV2
          open={ticketsDialogOpen}
          onClose={handleTicketsDialogClose}
          title={"Tickets Recreated"}
          dialogActions={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <CustomButtonV2
                btnName="Close"
                handleOnClick={handleTicketsDialogClose}
                type="cancel"
              />
              <CustomButtonV2
                btnName="View"
                handleOnClick={() => {
                  navigate(`/dashboard/v2/type-approval/tickets`);
                  setTicketsDialogOpen(false);
                }}
              />
            </Box>
          }
        >
          <Box
            sx={{
              width: "350px",
              height: "50px",
              alignItems: "center",
              display: "flex",
            }}
          >
            <Typography
              sx={{
                fontSize: "15px",
              }}
            >
              Successfully created the tickets for the intersections!
            </Typography>
          </Box>
        </CustomDialogBoxV2>
        <Dialog
          open={showDecisionMatrix}
          onClose={handelCloseDecisionMatrix}
          fullScreen
          PaperProps={{
            sx: {
              width: "100vw",
              height: "100vh",
              margin: 0,
              borderRadius: 0,
            },
          }}
        >
          <AppBar
            position="sticky"
            sx={{
              backgroundColor: "#26890D",
            }}
          >
            <Toolbar>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Decision Matrix
              </Typography>
              <IconButton color="inherit" onClick={handelCloseDecisionMatrix}>
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>

          <Box
            sx={{
              flex: 1,
              height: "calc(100vh - 64px)",
              overflow: "auto",
              p: 2,
            }}
          >
            {showDecisionMatrix && (
              <DecisionMatrixPanel
                context={decisionMatrixContext}
                key={showDecisionMatrix ? "open" : "closed"}
                onExecuteWorkflow={handleExecuteWorkflow}
                onViewExecution={handleViewExecution}
                onClose={handelCloseDecisionMatrix}
                onDecisionMatrixAction={
                  replaceComponentAndCheckForDecisionMatrix
                }
                handelGetSimilerTickets={handelGetSimilerTickets}
                similarTickets={similarTickets}
                setSimilarTickets={setSimilarTickets}
                entityDetails={selectedEntity}
                handelGetAiSuggestion={handelGetAiSuggestion}
                selectedTicket={selectedTicket}
                setSelectedTicket={setSelectedTicket}
                openAiPanel={openAiPanel}
                setOpenAiPanel={setOpenAiPanel}
                setCreateTicketData={setCreateTicketData}
                disabledStandards={disabledStandards}
              />
            )}
          </Box>
        </Dialog>
      </Box>
    </Box>
  );
}

VlcEEComponents.propTypes = {
  getZoneEECountList: PropTypes.func,
  getEEComponentsList: PropTypes.func,

  variantOptionList: PropTypes.array,
  eEComponentsList: PropTypes.array,
  swmListToFilter: PropTypes.array,
  filteredSWMList: PropTypes.array,
  sortedSWMList: PropTypes.array,

  heading: PropTypes.string,
  searchFieldValue: PropTypes.string,
  selectedSearchField: PropTypes.string,
  swmNameFilter: PropTypes.string,
  swmVersionFilter: PropTypes.string,
  swmNameFilterSearch: PropTypes.string,
  swmVersionFilterSearch: PropTypes.string,
  swmSortField: PropTypes.string,
  swmSortOrder: PropTypes.string,

  selectedSingleVariantDetails: PropTypes.object,
  selectedProjectDetails: PropTypes.object,
  selectedZone: PropTypes.object,
  selectedZoneDetails: PropTypes.object,
  selectedEEComponentDetails: PropTypes.object,

  handleEEComponentAdd: PropTypes.func,
  handleEEComponentUpdate: PropTypes.func,
  handleEEComponentReplace: PropTypes.func,
  setIsLinkSoftwareModulesScreen: PropTypes.func,
  setSearchFieldValue: PropTypes.func,
  setSelectedSearchField: PropTypes.func,
  setPage: PropTypes.func,
  setRowsPerPage: PropTypes.func,
  setHasMore: PropTypes.func,
  setSwmPage: PropTypes.func,
  setSwmRowsPerPage: PropTypes.func,
  setSwmHasMore: PropTypes.func,
  setSWMNameFilter: PropTypes.func,
  setSWMVersionFilter: PropTypes.func,
  setSWMNameFilterSearch: PropTypes.func,
  setSWMVersionFilterSearch: PropTypes.func,
  setSWMSortField: PropTypes.func,
  setSWMSortOrder: PropTypes.func,
  setSelectedEEComponentDetails: PropTypes.func,
  setTotalEECount: PropTypes.func,
  setTotalSWMCount: PropTypes.func,

  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  hasMore: PropTypes.bool,

  swmPage: PropTypes.number,
  swmRowsPerPage: PropTypes.number,
  swmHasMore: PropTypes.bool,

  totalEECount: PropTypes.number,
  totalSWMCount: PropTypes.number,

  viewArchived: PropTypes.bool,
  selectedArchivedEEs: PropTypes.arrayOf(PropTypes.any),
  setSelectedArchivedEEs: PropTypes.func,
  selectedArchivedSWMs: PropTypes.arrayOf(PropTypes.any),
  setSelectedArchivedSWMs: PropTypes.func,
  clearAllCounter: PropTypes.number,
  retrievalRefreshCounter: PropTypes.number,
};

VlcEEComponentUpdateDialog.propTypes = {
  onRedirectedActionDialogClose: PropTypes.func,
  variantOptionList: PropTypes.array,

  openEEComponentUpdateDialog: PropTypes.bool,
  setOpenEEComponentUpdateDialog: PropTypes.func,

  handleEEComponentUpdate: PropTypes.func,
  setSelectedEEComponentUpdateDetails: PropTypes.func,
  setIsLinkSoftwareModulesScreen: PropTypes.func,
  getEEComponentsList: PropTypes.func,
  getSWModulesList: PropTypes.func,
  setIsVersionImpactFlow: PropTypes.func,

  selectedEEComponentUpdateDetails: PropTypes.object,
  selectedZone: PropTypes.object,
  selectedEEComponentDetails: PropTypes.object,
  selectedProjectDetails: PropTypes.object,
  isVersionImpactFlow: PropTypes.bool,
  resumeAttributeImpactFlowTrigger: PropTypes.number,
  resumeVersionImpactFlowTrigger: PropTypes.number,

  selectedSingleVariantDetails: PropTypes.object,
};

VlcEEComponentReplaceDialog.propTypes = {
  title: PropTypes.string,

  openEEComponentReplaceDialog: PropTypes.bool,
  setOpenEEComponentReplaceDialog: PropTypes.func,

  getZoneEECountList: PropTypes.func,
  getEEComponentsList: PropTypes.func,
  getSWModulesList: PropTypes.func,

  handleEEComponentReplace: PropTypes.func,
  setSelectedEEComponentReplaceDetails: PropTypes.func,
  setIsLinkSoftwareModulesScreen: PropTypes.func,

  vsOptionsList: PropTypes.array,
  variantOptionList: PropTypes.array,
  supplierOptionsList: PropTypes.array,

  selectedEEComponentReplaceDetails: PropTypes.object,
  selectedZone: PropTypes.object,
  selectedZoneDetails: PropTypes.object,
  selectedEEComponentDetails: PropTypes.object,
  selectedProjectDetails: PropTypes.object,
  selectedSingleVariantDetails: PropTypes.object,
};
